// Custom Graph Renderer
// Grammar of Graphics準拠のグラフレンダラー（複数レイヤー対応）

import React from 'react';
import * as d3 from 'd3';
import { DataSet, ColumnId, DataValue } from '../../../../types';
import {
  CustomGraphConfig,
  AesValue,
  LayerDef,
  PointGeomDef,
  LineGeomDef,
  BarGeomDef,
  AreaGeomDef,
  TileGeomDef,
  ScaleType,
  AxisScaleDef,
  StatDef,
  SmoothStatDef,
  PositionDef,
  isContinuousScale,
  isCategoricalScale,
  getCategoricalDomain,
} from '../../../../types/custom-graph.types';
import { useD3Chart } from '../../../hooks/useD3Chart';
import { COLORS } from '../../../constants/colors';
import { ClearSelectionButton } from '../../common/ClearSelectionButton';
import {
  type DataPoint,
  applyCountTransform,
  applyBinTransform,
  applyDensityTransform,
  applySummaryTransform,
  applyLinearRegressionSmooth,
  applyLoessSmooth,
  applyJitterPosition,
  applyDodgePosition,
  applyStackPosition,
  applyFillPosition,
} from '../../../utils/statistics';
import {
  createCategoricalScale as createColorScale,
  createSequentialScale,
  createDivergingScale,
  DEFAULT_PALETTE_IDS,
} from '../../../constants/palette-utils';

/**
 * 統計変換を適用
 * @param points データポイント配列
 * @param stat 統計変換定義
 * @param sharedXYValues Issue#241: Facet complete使用時の共有X/Y値
 */
function applyStatTransform(
  points: DataPoint[],
  stat: StatDef | undefined,
  sharedXYValues?: {
    xValues?: unknown[];
    yValues?: unknown[];
  }
): DataPoint[] {
  if (!stat || stat.type === 'identity') {
    return points;
  }

  switch (stat.type) {
    case 'count':
      // Count統計は任意の型のXに対応
      return applyCountTransform(points);

    case 'bin':
      // Bin統計は数値Xのみ（実行時にチェック済み）
      return applyBinTransform(
        points as DataPoint<number>[],
        stat.params?.bins,
        stat.params?.binwidth,
        stat.params?.yScale
      );

    case 'smooth':
      return applySmoothStat(points, stat);

    case 'density':
      // Density統計は数値Xのみ（実行時にチェック済み）
      return applyDensityTransform(
        points as DataPoint<number>[],
        stat.params?.bandwidth,
        stat.params?.yScale || 'density'
      );

    case 'summary': {
      // Summary統計: groupByでグループ化し、inputの値を集約してoutputに出力
      // デフォルト: input='y', output='y'（棒グラフ用）
      // ヒートマップの場合: input='fill', output='fill'（fillに数値をマッピング時）
      // Issue#241: Facet complete処理
      // - sharedXYValuesがオブジェクト: Facetコンテキスト（Statistics-side completeは無効）
      // - sharedXYValuesがundefined: 非Facet（Statistics-side completeが有効）
      const isFacetContext = sharedXYValues !== undefined;
      const hasFacetCompleteValues = sharedXYValues?.xValues !== undefined;
      return applySummaryTransform(
        points,
        stat.params?.fun || 'mean',
        {
          groupBy: stat.groupBy,
          input: stat.input,
          output: stat.output,
          // Facetコンテキスト: Facet completeのみ有効（値がある場合）
          // 非Facetコンテキスト: Statistics-side completeが有効
          complete: isFacetContext ? hasFacetCompleteValues : stat.params?.complete,
          sharedXValues: sharedXYValues?.xValues,
          sharedYValues: sharedXYValues?.yValues,
        }
      );
    }

    default:
      return points;
  }
}

/**
 * Smooth統計: 平滑化曲線を計算
 */
function applySmoothStat(points: DataPoint[], stat: SmoothStatDef): DataPoint[] {
  if (points.length < 2) return points;

  // Smooth統計は数値Xのみ（実行時にチェック済み）
  const numericPoints = points as DataPoint<number>[];
  const method = stat.params?.method || 'loess';

  switch (method) {
    case 'lm':
      return applyLinearRegressionSmooth(numericPoints);

    case 'loess':
    case 'gam': // GAMは複雑なので、loessで代用
      return applyLoessSmooth(numericPoints, stat.params?.span || 0.75);

    default:
      return points;
  }
}

/**
 * データポイントが完全に選択されているかをチェック（Point/Line/Area用）
 * Statistics変換使用時は全ての元データ行が選択されている場合のみtrue
 */
function isPointFullySelected(point: DataPoint, selectedRows: number[]): boolean {
  if (point.originalIndices) {
    // Statistics変換使用時: 全ての元のインデックスが選択されているか
    // 注意: 空配列の場合は明示的にfalseを返す必要がある
    // - completeオプション等で生成された仮想ポイントはoriginalIndices: []を持つ
    // - JavaScriptの[].every()は常にtrueを返す（vacuous truth）ため、
    //   この明示的チェックがないと仮想ポイントが「完全選択」扱いになってしまう
    if (point.originalIndices.length === 0) {
      return false;
    }
    return point.originalIndices.every(idx => selectedRows.includes(idx));
  }
  // 通常時: indexが選択されているか
  return selectedRows.includes(point.index);
}

/**
 * 選択されている元データ行の数を取得（Bar用、ツールチップ用）
 */
function getSelectedCount(point: DataPoint, selectedRows: number[]): number {
  if (point.originalIndices) {
    return point.originalIndices.filter(idx => selectedRows.includes(idx)).length;
  }
  return selectedRows.includes(point.index) ? 1 : 0;
}

/**
 * Geometry描画の共通コンテキスト
 * 各Geometry描画関数に渡される共通のパラメータセット
 */
interface GeometryRenderContext {
  // D3選択とデータ
  layerGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  points: DataPoint[];

  // レイヤー設定
  layer: LayerDef;
  scales: {
    color?: d3.ScaleOrdinal<string | number, string>;
    fill?: d3.ScaleOrdinal<string | number, string>;
    fillContinuous?: d3.ScaleSequential<string> | d3.ScaleDiverging<string>;
    stroke?: d3.ScaleOrdinal<string | number, string>;
    size?: d3.ScaleLinear<number, number>;
    shape?: d3.ScaleOrdinal<string | number, string>;
    alpha?: d3.ScaleLinear<number, number>;
    linetype?: d3.ScaleOrdinal<string | number, string>;
  };

  // 座標系・スケール
  coordType: 'cartesian' | 'flip';
  effectiveIsXCategorical: boolean;
  effectiveIsYCategorical: boolean;
  xScaleCategorical: d3.ScaleBand<string> | null;
  yScaleCategorical: d3.ScaleBand<string> | null;
  xScaleNumeric: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number> | null;
  yScaleNumeric: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number> | null;
  currentXScale: d3.ScaleLinear<number, number> | d3.ScaleBand<string> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number>;
  currentYScale: d3.ScaleLinear<number, number> | d3.ScaleBand<string> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number>;

  // 座標変換関数
  getX: (d: { x: string | number; y: number }) => string | number;
  getY: (d: { x: string | number; y: number }) => string | number;
  callXScale: (value: string | number) => number;
  callYScale: (value: string | number) => number;

  // 選択状態
  selectedRows: number[];
  onPointClick?: (rowIndices: number[], isAdditive: boolean) => void;

  // 固定塗りつぶし色（aesthetic.fillがHex色の場合に使用）
  fixedFillColor?: string;
}

/**
 * Point Geometryの描画
 */
function renderPointGeometry(context: GeometryRenderContext): void {
  const {
    layerGroup,
    points,
    layer,
    scales,
    getX,
    getY,
    callXScale,
    callYScale,
    selectedRows,
    onPointClick,
  } = context;

  const pointGeom = layer.geom as PointGeomDef;
  const geomDefaults = pointGeom.defaults || {};
  const sizeScale = scales.size;
  const alphaScale = scales.alpha;
  const strokeScale = scales.stroke;
  const colorScale = scales.color;

  const circles = layerGroup
    .selectAll<SVGCircleElement, typeof points[0]>('circle')
    .data(points, d => d.index);

  circles.exit().remove();

  const enter = circles.enter()
    .append('circle')
    .style('cursor', 'pointer')
    .on('click', function(event, d) {
      if (onPointClick) {
        const isAdditive = event.ctrlKey || event.metaKey || event.shiftKey;
        // Statistics変換使用時はoriginalIndicesを使用
        const indices = d.originalIndices ?? [d.index];
        onPointClick(indices, isAdditive);
      }
    });

  circles.merge(enter)
    .attr('cx', d => callXScale(getX(d)))
    .attr('cy', d => callYScale(getY(d)))
    .attr('r', d => {
      // sizeチャネルが指定されている場合はスケールを使用
      if (sizeScale && d.size != null) {
        return sizeScale(d.size);
      }
      return geomDefaults.size ?? 5;
    })
    .attr('fill', d => {
      if (isPointFullySelected(d, selectedRows)) {
        return COLORS.selection.primary;
      }
      // colorチャネルが指定されている場合はスケールを使用
      if (colorScale && d.color != null) {
        return colorScale(String(d.color));
      }
      return geomDefaults.color ?? 'steelblue';
    })
    .attr('opacity', d => {
      if (selectedRows.length > 0 && !isPointFullySelected(d, selectedRows)) {
        return 0.2;
      }
      // alphaチャネルが指定されている場合はスケールを使用
      if (alphaScale && d.alpha != null) {
        return alphaScale(d.alpha);
      }
      return geomDefaults.alpha ?? 0.7;
    })
    .attr('stroke', d => {
      if (isPointFullySelected(d, selectedRows)) {
        return '#000';
      }
      // strokeチャネルが指定されている場合はスケールを使用
      if (strokeScale && d.stroke != null) {
        return strokeScale(String(d.stroke));
      }
      return 'none';
    })
    .attr('stroke-width', d => {
      if (isPointFullySelected(d, selectedRows)) {
        return 2;
      }
      return 0;
    });
}

/**
 * Line Geometryの描画
 */
function renderLineGeometry(context: GeometryRenderContext): void {
  const {
    layerGroup,
    points,
    layer,
    scales,
    getX,
    getY,
    callXScale,
    callYScale,
  } = context;

  const lineGeom = layer.geom as LineGeomDef;
  const geomDefaults = lineGeom.defaults || {};
  const strokeWidth = geomDefaults.strokeWidth ?? 2;
  const lineType = geomDefaults.lineType ?? 'solid';
  const alpha = geomDefaults.alpha ?? 0.9;
  let colorScale = scales.color;
  const alphaScale = scales.alpha;

  // データポイントをx軸順にソート（Line geometryは数値Xが前提）
  const sortedPoints = [...points].sort((a, b) => (a.x as number) - (b.x as number));

  // カテゴリごとにグループ化（データポイントにcolor値がある場合）
  const groupedData: Map<string | number, typeof points> = new Map();
  const hasColorValues = sortedPoints.some(p => p.color != null);

  if (hasColorValues) {
    // データポイントにcolor値がある場合はグループ化
    sortedPoints.forEach(point => {
      const category = point.color ?? 'default';
      if (!groupedData.has(category)) {
        groupedData.set(category, []);
      }
      groupedData.get(category)!.push(point);
    });

    // スケールがない場合はデフォルトパレットを作成
    // (統計変換でcolorプロパティが追加された場合に対応)
    if (!colorScale) {
      const uniqueCategories = Array.from(groupedData.keys()).filter(k => k !== 'default');
      if (uniqueCategories.length > 0) {
        colorScale = createColorScale(DEFAULT_PALETTE_IDS.categorical, uniqueCategories.map(String));
      }
    }
  } else {
    groupedData.set('default', sortedPoints);
  }

  // 線の生成器
  const line = d3.line<typeof points[0]>()
    .x(d => callXScale(getX(d)))
    .y(d => callYScale(getY(d)));

  // 各カテゴリの線を描画
  const paths = layerGroup
    .selectAll<SVGPathElement, [string | number, typeof points]>('path')
    .data(Array.from(groupedData.entries()), d => String(d[0]));

  paths.exit().remove();

  const enterPaths = paths.enter()
    .append('path')
    .style('fill', 'none')
    .style('cursor', 'pointer');

  paths.merge(enterPaths)
    .attr('d', d => line(d[1]))
    .attr('stroke', d => {
      const category = d[0];
      if (colorScale && category !== 'default') {
        return colorScale(String(category));
      }
      return geomDefaults.color ?? 'steelblue';
    })
    .attr('stroke-width', strokeWidth)
    .attr('stroke-dasharray', lineType === 'dashed' ? '5,5' : lineType === 'dotted' ? '2,2' : 'none')
    .attr('opacity', d => {
      // グループの最初のポイントのalpha値を使用
      const firstPoint = d[1][0];
      if (alphaScale && firstPoint?.alpha != null) {
        return alphaScale(firstPoint.alpha);
      }
      return alpha;
    });
}

/**
 * Bar Geometryの描画
 */
function renderBarGeometry(context: GeometryRenderContext): void {
  const {
    layerGroup,
    points,
    layer,
    scales,
    coordType,
    effectiveIsXCategorical,
    effectiveIsYCategorical,
    xScaleCategorical,
    yScaleCategorical,
    xScaleNumeric,
    getX,
    getY,
    callXScale,
    callYScale,
    selectedRows,
    onPointClick,
  } = context;

  const barGeom = layer.geom as BarGeomDef;
  const geomDefaults = barGeom.defaults || {};
  const barWidth = geomDefaults.width ?? 0.8;
  const alpha = geomDefaults.alpha ?? 0.8;
  const fillScale = scales.fill;
  const strokeScale = scales.stroke;
  const colorScale = scales.color;
  const alphaScale = scales.alpha;

  // バーの幅を計算
  let baseBarWidth = 10; // デフォルト幅

  // X軸がカテゴリカルな場合はbandwidth()を使用
  if (effectiveIsXCategorical && xScaleCategorical) {
    baseBarWidth = xScaleCategorical.bandwidth() * barWidth;
  } else if (xScaleNumeric) {
    // 数値軸の場合は、データポイント間の最小距離を基準に計算
    const sortedPoints = [...points].sort((a, b) => (getX(a) as number) - (getX(b) as number));
    if (sortedPoints.length > 1) {
      const gaps = [];
      for (let i = 1; i < sortedPoints.length; i++) {
        const gap = callXScale(getX(sortedPoints[i])) - callXScale(getX(sortedPoints[i - 1]));
        if (gap > 0) gaps.push(gap);
      }
      if (gaps.length > 0) {
        baseBarWidth = Math.min(...gaps) * barWidth;
      }
    }
  }

  // Y軸がカテゴリカルな場合もbandwidth()を使用
  if (effectiveIsYCategorical && yScaleCategorical) {
    baseBarWidth = yScaleCategorical.bandwidth() * barWidth;
  } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
    // フリップされた数値ヒストグラム：Y軸の数値間隔を基準に高さを計算
    const sortedPoints = [...points].sort((a, b) => (getY(a) as number) - (getY(b) as number));
    if (sortedPoints.length > 1) {
      const gaps = [];
      for (let i = 1; i < sortedPoints.length; i++) {
        const gap = Math.abs(callYScale(getY(sortedPoints[i])) - callYScale(getY(sortedPoints[i - 1])));
        if (gap > 0) gaps.push(gap);
      }
      if (gaps.length > 0) {
        baseBarWidth = Math.min(...gaps) * barWidth;
      }
    }
  }

  // 1. 全体バーの描画（常に全データ）
  const allBars = layerGroup
    .selectAll<SVGRectElement, typeof points[0]>('.bar-all')
    .data(points, d => `all-${d.index}`);

  allBars.exit().remove();

  const enterAllBars = allBars.enter()
    .append('rect')
    .attr('class', 'bar-all')
    .style('cursor', 'pointer')
    .on('click', function(event, d) {
      if (onPointClick) {
        const isAdditive = event.ctrlKey || event.metaKey || event.shiftKey;
        // Statistics変換使用時はoriginalIndicesを使用
        const indices = d.originalIndices ?? [d.index];
        onPointClick(indices, isAdditive);
      }
    });

  allBars.merge(enterAllBars)
    .attr('x', d => {
      if (effectiveIsXCategorical && xScaleCategorical) {
        const bandStart = xScaleCategorical(String(getX(d))) ?? 0;
        const bandwidth = xScaleCategorical.bandwidth();
        return bandStart + (bandwidth - baseBarWidth) / 2;
      } else if (effectiveIsYCategorical && yScaleCategorical) {
        // 横向きバーグラフの場合は0から開始
        const x0 = callXScale(0);
        const x1 = callXScale(getX(d));
        return Math.min(x0, x1);
      } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
        // フリップされた数値ヒストグラム（横向き）：0から開始
        const x0 = callXScale(0);
        const x1 = callXScale(getX(d));
        return Math.min(x0, x1);
      } else {
        return callXScale(getX(d)) - baseBarWidth / 2;
      }
    })
    .attr('y', d => {
      if (effectiveIsYCategorical && yScaleCategorical) {
        const bandStart = yScaleCategorical(String(getY(d))) ?? 0;
        const bandwidth = yScaleCategorical.bandwidth();
        return bandStart + (bandwidth - baseBarWidth) / 2;
      } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
        // フリップされた数値ヒストグラム（横向き）：Y位置を中央に配置
        return callYScale(getY(d)) - baseBarWidth / 2;
      } else {
        // Stack position使用時はymin/ymaxを使用
        if (d.ymin != null && d.ymax != null) {
          const y0 = callYScale(d.ymin);
          const y1 = callYScale(d.ymax);
          return Math.min(y0, y1);
        } else {
          const y0 = callYScale(0);
          const y1 = callYScale(getY(d));
          return (getY(d) as number) >= 0 ? y1 : y0;
        }
      }
    })
    .attr('width', d => {
      if (effectiveIsYCategorical && yScaleCategorical) {
        const x0 = callXScale(0);
        const x1 = callXScale(getX(d));
        return Math.abs(x1 - x0);
      } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
        // フリップされた数値ヒストグラム（横向き）：幅は値に比例
        const x0 = callXScale(0);
        const x1 = callXScale(getX(d));
        return Math.abs(x1 - x0);
      } else {
        return baseBarWidth;
      }
    })
    .attr('height', d => {
      if (effectiveIsYCategorical && yScaleCategorical) {
        return baseBarWidth;
      } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
        // フリップされた数値ヒストグラム（横向き）：高さは固定
        return baseBarWidth;
      } else {
        // Stack position使用時はymin/ymaxを使用
        if (d.ymin != null && d.ymax != null) {
          const y0 = callYScale(d.ymin);
          const y1 = callYScale(d.ymax);
          return Math.abs(y0 - y1);
        } else {
          const y0 = callYScale(0);
          const y1 = callYScale(getY(d));
          return Math.abs(y0 - y1);
        }
      }
    })
    .attr('fill', d => {
      // fill aestheticのみ使用
      if (fillScale && d.fill != null) {
        return fillScale(String(d.fill));
      }
      return geomDefaults.color ?? 'steelblue';
    })
    .attr('opacity', d => {
      if (selectedRows.length > 0) {
        return 0.3;
      }
      // alphaチャネルが指定されている場合はスケールを使用
      if (alphaScale && d.alpha != null) {
        return alphaScale(d.alpha);
      }
      return alpha;
    })
    .attr('stroke', d => {
      // Bar geometryではcolorは使用しない（fillのみ）
      if (strokeScale && d.stroke != null) {
        return strokeScale(String(d.stroke));
      }
      return 'none';
    })
    .attr('stroke-width', d => {
      // strokeが設定されている場合のみ枠線を表示
      if (strokeScale && d.stroke != null) {
        return 1;
      }
      return 0;
    });

  // 2. 部分選択バー（一部の行が選択されている場合）
  if (selectedRows.length > 0) {
    const partialBars = layerGroup
      .selectAll<SVGRectElement, typeof points[0]>('.bar-partial')
      .data(
        points.filter(d => {
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          return selectedCount > 0 && selectedCount < totalCount;
        }),
        d => `partial-${d.index}`
      );

    partialBars.exit().remove();

    const enterPartialBars = partialBars.enter()
      .append('rect')
      .attr('class', 'bar-partial')
      .style('pointer-events', 'none'); // クリックイベントは全体バーで処理

    partialBars.merge(enterPartialBars)
      .attr('x', d => {
        if (effectiveIsXCategorical && xScaleCategorical) {
          const bandStart = xScaleCategorical(String(getX(d))) ?? 0;
          const bandwidth = xScaleCategorical.bandwidth();
          return bandStart + (bandwidth - baseBarWidth) / 2;
        } else if (effectiveIsYCategorical && yScaleCategorical) {
          // 横向きバーグラフの場合は0から開始
          return callXScale(0);
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：0から開始
          return callXScale(0);
        } else {
          return callXScale(getX(d)) - baseBarWidth / 2;
        }
      })
      .attr('y', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          const bandStart = yScaleCategorical(String(getY(d))) ?? 0;
          const bandwidth = yScaleCategorical.bandwidth();
          return bandStart + (bandwidth - baseBarWidth) / 2;
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：Y位置を中央に配置
          return callYScale(getY(d)) - baseBarWidth / 2;
        } else {
          // 選択数に応じた高さを計算
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          // Stack position使用時はymin/ymaxを使用
          if (d.ymin != null && d.ymax != null) {
            const segmentHeight = d.ymax - d.ymin;
            const selectedYmax = d.ymin + (segmentHeight * selectedCount) / totalCount;
            return callYScale(selectedYmax);
          } else {
            const selectedY = ((getY(d) as number) * selectedCount) / totalCount;
            return callYScale(selectedY);
          }
        }
      })
      .attr('width', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          // 横棒グラフの場合、widthを選択率に応じて変化
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          const selectedX = ((getX(d) as number) * selectedCount) / totalCount;
          const x0 = callXScale(0);
          const x1 = callXScale(selectedX);
          return Math.abs(x1 - x0);
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：幅を選択率に応じて変化
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          const selectedX = ((getX(d) as number) * selectedCount) / totalCount;
          const x0 = callXScale(0);
          const x1 = callXScale(selectedX);
          return Math.abs(x1 - x0);
        } else {
          return baseBarWidth;
        }
      })
      .attr('height', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          return baseBarWidth;
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：高さは固定
          return baseBarWidth;
        } else {
          // 縦棒グラフの場合、heightを選択数に応じて変化
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          // Stack position使用時はymin/ymaxを使用
          if (d.ymin != null && d.ymax != null) {
            const segmentHeight = d.ymax - d.ymin;
            const selectedYmax = d.ymin + (segmentHeight * selectedCount) / totalCount;
            const y0 = callYScale(d.ymin);
            const y1 = callYScale(selectedYmax);
            return Math.abs(y0 - y1);
          } else {
            const selectedY = ((getY(d) as number) * selectedCount) / totalCount;
            const y0 = callYScale(0);
            const y1 = callYScale(selectedY);
            return Math.abs(y0 - y1);
          }
        }
      })
      .attr('fill', d => {
        // fill aestheticのみ使用
        if (fillScale && d.fill != null) {
          return fillScale(String(d.fill));
        }
        return geomDefaults.color ?? 'steelblue';
      })
      .attr('opacity', d => {
        // alphaチャネルが指定されている場合はスケールを使用
        if (alphaScale && d.alpha != null) {
          return alphaScale(d.alpha);
        }
        return alpha;
      })
      .attr('stroke', d => {
        // stroke aestheticを優先、次にcolor、最後にデフォルト
        if (strokeScale && d.stroke != null) {
          return strokeScale(String(d.stroke));
        } else if (colorScale && d.color != null) {
          return colorScale(String(d.color));
        }
        return 'none'; // デフォルトは枠線なし
      })
      .attr('stroke-width', d => {
        // stroke/colorが設定されている場合のみ枠線を表示
        if ((strokeScale && d.stroke != null) || (colorScale && d.color != null)) {
          return 1;
        }
        return 0;
      });

    // 3. 完全選択バー（すべての行が選択されている場合）
    const selectedBars = layerGroup
      .selectAll<SVGRectElement, typeof points[0]>('.bar-selected')
      .data(
        points.filter(d => {
          const selectedCount = getSelectedCount(d, selectedRows);
          const totalCount = d.originalIndices ? d.originalIndices.length : 1;
          return selectedCount === totalCount;
        }),
        d => `selected-${d.index}`
      );

    selectedBars.exit().remove();

    const enterSelectedBars = selectedBars.enter()
      .append('rect')
      .attr('class', 'bar-selected')
      .style('pointer-events', 'none'); // クリックイベントは全体バーで処理

    selectedBars.merge(enterSelectedBars)
      .attr('x', d => {
        if (effectiveIsXCategorical && xScaleCategorical) {
          const bandStart = xScaleCategorical(String(getX(d))) ?? 0;
          const bandwidth = xScaleCategorical.bandwidth();
          return bandStart + (bandwidth - baseBarWidth) / 2;
        } else if (effectiveIsYCategorical && yScaleCategorical) {
          // 横向きバーグラフの場合は0から開始
          const x0 = callXScale(0);
          const x1 = callXScale(getX(d));
          return Math.min(x0, x1);
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：0から開始
          const x0 = callXScale(0);
          const x1 = callXScale(getX(d));
          return Math.min(x0, x1);
        } else {
          return callXScale(getX(d)) - baseBarWidth / 2;
        }
      })
      .attr('y', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          const bandStart = yScaleCategorical(String(getY(d))) ?? 0;
          const bandwidth = yScaleCategorical.bandwidth();
          return bandStart + (bandwidth - baseBarWidth) / 2;
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：Y位置を中央に配置
          return callYScale(getY(d)) - baseBarWidth / 2;
        } else {
          // 完全選択の場合は元の高さをそのまま使用
          // Stack position使用時はymin/ymaxを使用
          if (d.ymin != null && d.ymax != null) {
            const y0 = callYScale(d.ymin);
            const y1 = callYScale(d.ymax);
            return Math.min(y0, y1);
          } else {
            const y0 = callYScale(0);
            const y1 = callYScale(getY(d));
            return (getY(d) as number) >= 0 ? y1 : y0;
          }
        }
      })
      .attr('width', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          // 横棒グラフの場合、widthは元の値をそのまま使用
          const x0 = callXScale(0);
          const x1 = callXScale(getX(d));
          return Math.abs(x1 - x0);
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：幅は値に比例
          const x0 = callXScale(0);
          const x1 = callXScale(getX(d));
          return Math.abs(x1 - x0);
        } else {
          return baseBarWidth;
        }
      })
      .attr('height', d => {
        if (effectiveIsYCategorical && yScaleCategorical) {
          return baseBarWidth;
        } else if (coordType === 'flip' && !effectiveIsXCategorical && !effectiveIsYCategorical) {
          // フリップされた数値ヒストグラム（横向き）：高さは固定
          return baseBarWidth;
        } else {
          // 縦棒グラフの場合、heightは元の値をそのまま使用
          // Stack position使用時はymin/ymaxを使用
          if (d.ymin != null && d.ymax != null) {
            const y0 = callYScale(d.ymin);
            const y1 = callYScale(d.ymax);
            return Math.abs(y0 - y1);
          } else {
            const y0 = callYScale(0);
            const y1 = callYScale(getY(d));
            return Math.abs(y0 - y1);
          }
        }
      })
      .attr('fill', d => {
        // fill aestheticのみ使用
        if (fillScale && d.fill != null) {
          return fillScale(String(d.fill));
        }
        return geomDefaults.color ?? 'steelblue';
      })
      .attr('opacity', d => {
        // alphaチャネルが指定されている場合はスケールを使用
        if (alphaScale && d.alpha != null) {
          return alphaScale(d.alpha);
        }
        return alpha;
      })
      .attr('stroke', d => {
        // stroke aestheticを優先、次にcolor、最後にデフォルト
        if (strokeScale && d.stroke != null) {
          return strokeScale(String(d.stroke));
        } else if (colorScale && d.color != null) {
          return colorScale(String(d.color));
        }
        return 'none'; // デフォルトは枠線なし
      })
      .attr('stroke-width', d => {
        // stroke/colorが設定されている場合のみ枠線を表示
        if ((strokeScale && d.stroke != null) || (colorScale && d.color != null)) {
          return 1;
        }
        return 0;
      });
  } else {
    // 選択がない場合は選択バーと部分選択バーを削除
    layerGroup.selectAll('.bar-selected').remove();
    layerGroup.selectAll('.bar-partial').remove();
  }
}

/**
 * Area Geometryの描画
 */
function renderAreaGeometry(context: GeometryRenderContext): void {
  const {
    layerGroup,
    points,
    layer,
    scales,
    getX,
    getY,
    callXScale,
    callYScale,
  } = context;

  const areaGeom = layer.geom as AreaGeomDef;
  const geomDefaults = areaGeom.defaults || {};
  const alpha = geomDefaults.alpha ?? 0.5;
  let fillScale = scales.fill;
  let strokeScale = scales.stroke;
  let colorScale = scales.color;
  const alphaScale = scales.alpha;

  // データポイントをx軸順にソート（Area geometryは数値Xが前提）
  const sortedPoints = [...points].sort((a, b) => (getX(a) as number) - (getX(b) as number));

  // カテゴリごとにグループ化（データポイントにfill値がある場合）
  const groupedData: Map<string | number, typeof points> = new Map();
  const hasFillValues = sortedPoints.some(p => p.fill != null);

  if (hasFillValues) {
    // データポイントにfill値がある場合はグループ化
    sortedPoints.forEach(point => {
      const category = point.fill ?? 'default';
      if (!groupedData.has(category)) {
        groupedData.set(category, []);
      }
      groupedData.get(category)!.push(point);
    });

    // スケールがない場合はデフォルトパレットを作成
    // (統計変換でfillプロパティが追加された場合に対応)
    if (!fillScale) {
      const uniqueCategories = Array.from(groupedData.keys()).filter(k => k !== 'default');
      if (uniqueCategories.length > 0) {
        fillScale = createColorScale(DEFAULT_PALETTE_IDS.categorical, uniqueCategories.map(String));
      }
    }
  } else {
    groupedData.set('default', sortedPoints);
  }

  // エリアの生成器
  const area = d3.area<typeof points[0]>()
    .x(d => callXScale(getX(d)))
    .y0(callYScale(0))
    .y1(d => callYScale(getY(d)));

  // 各カテゴリのエリアを描画
  const paths = layerGroup
    .selectAll<SVGPathElement, [string | number, typeof points]>('path')
    .data(Array.from(groupedData.entries()), d => String(d[0]));

  paths.exit().remove();

  const enterPaths = paths.enter()
    .append('path')
    .style('cursor', 'pointer');

  paths.merge(enterPaths)
    .attr('d', d => area(d[1]))
    .attr('fill', d => {
      const category = d[0];
      if (fillScale && category !== 'default') {
        return fillScale(String(category));
      }
      return geomDefaults.color ?? 'steelblue';
    })
    .attr('stroke', d => {
      const category = d[0];
      // stroke aestheticを優先、次にcolor
      if (strokeScale && category !== 'default') {
        return strokeScale(String(category));
      } else if (colorScale && category !== 'default') {
        return colorScale(String(category));
      }
      return 'none'; // デフォルトは枠線なし
    })
    .attr('stroke-width', d => {
      const category = d[0];
      // stroke/colorが設定されている場合のみ枠線を表示
      if ((strokeScale && category !== 'default') || (colorScale && category !== 'default')) {
        return 1;
      }
      return 0;
    })
    .attr('opacity', d => {
      // グループの最初のポイントのalpha値を使用
      const firstPoint = d[1][0];
      if (alphaScale && firstPoint?.alpha != null) {
        return alphaScale(firstPoint.alpha);
      }
      return alpha;
    });
}

/**
 * Tile Geometryの描画（ヒートマップ用）
 */
function renderTileGeometry(context: GeometryRenderContext): void {
  const {
    layerGroup,
    points,
    layer,
    scales,
    effectiveIsXCategorical,
    effectiveIsYCategorical,
    xScaleCategorical,
    yScaleCategorical,
    getX,
    getY,
    callXScale,
    callYScale,
    selectedRows,
    onPointClick,
    fixedFillColor,
  } = context;

  const tileGeom = layer.geom as TileGeomDef;
  const geomDefaults = tileGeom.defaults || {};
  const alpha = geomDefaults.alpha ?? 1.0;
  const stroke = geomDefaults.stroke ?? '#ffffff';
  const strokeWidth = geomDefaults.strokeWidth ?? 0.5;
  const fillScale = scales.fill;
  const fillContinuousScale = scales.fillContinuous;
  const alphaScale = scales.alpha;

  // タイルの幅と高さを計算
  let tileWidth = geomDefaults.width ?? 10;
  let tileHeight = geomDefaults.height ?? 10;

  // X軸がカテゴリカルな場合はbandwidth()を使用
  if (effectiveIsXCategorical && xScaleCategorical) {
    tileWidth = xScaleCategorical.bandwidth();
  } else {
    // 数値軸の場合は、データポイント間の最小距離を基準に計算
    const sortedByX = [...points].sort((a, b) => (getX(a) as number) - (getX(b) as number));
    if (sortedByX.length > 1) {
      const gaps: number[] = [];
      for (let i = 1; i < sortedByX.length; i++) {
        const gap = callXScale(getX(sortedByX[i])) - callXScale(getX(sortedByX[i - 1]));
        if (gap > 0) gaps.push(gap);
      }
      if (gaps.length > 0) {
        tileWidth = Math.min(...gaps);
      }
    }
  }

  // Y軸がカテゴリカルな場合はbandwidth()を使用
  if (effectiveIsYCategorical && yScaleCategorical) {
    tileHeight = yScaleCategorical.bandwidth();
  } else {
    // 数値軸の場合は、データポイント間の最小距離を基準に計算
    const sortedByY = [...points].sort((a, b) => (getY(a) as number) - (getY(b) as number));
    if (sortedByY.length > 1) {
      const gaps: number[] = [];
      for (let i = 1; i < sortedByY.length; i++) {
        const gap = Math.abs(callYScale(getY(sortedByY[i])) - callYScale(getY(sortedByY[i - 1])));
        if (gap > 0) gaps.push(gap);
      }
      if (gaps.length > 0) {
        tileHeight = Math.min(...gaps);
      }
    }
  }

  // タイルの描画
  const tiles = layerGroup
    .selectAll<SVGRectElement, typeof points[0]>('rect.tile')
    .data(points, d => `tile-${d.index}`);

  tiles.exit().remove();

  const enterTiles = tiles.enter()
    .append('rect')
    .attr('class', 'tile')
    .style('cursor', 'pointer')
    .on('click', function(event, d) {
      if (onPointClick) {
        const isAdditive = event.ctrlKey || event.metaKey || event.shiftKey;
        const indices = d.originalIndices ?? [d.index];
        onPointClick(indices, isAdditive);
      }
    });

  tiles.merge(enterTiles)
    .attr('x', d => {
      if (effectiveIsXCategorical && xScaleCategorical) {
        return xScaleCategorical(String(getX(d))) ?? 0;
      } else {
        return callXScale(getX(d)) - tileWidth / 2;
      }
    })
    .attr('y', d => {
      if (effectiveIsYCategorical && yScaleCategorical) {
        return yScaleCategorical(String(getY(d))) ?? 0;
      } else {
        return callYScale(getY(d)) - tileHeight / 2;
      }
    })
    .attr('width', tileWidth)
    .attr('height', tileHeight)
    .attr('fill', d => {
      // 選択状態の場合
      if (isPointFullySelected(d, selectedRows)) {
        return COLORS.selection.primary;
      }
      // 連続色スケールを優先
      if (fillContinuousScale && d.fill != null && typeof d.fill === 'number') {
        return fillContinuousScale(d.fill);
      }
      // カテゴリカル色スケール
      if (fillScale && d.fill != null) {
        return fillScale(String(d.fill));
      }
      // 固定色が指定されている場合
      if (fixedFillColor) {
        return fixedFillColor;
      }
      return '#cccccc';
    })
    .attr('opacity', d => {
      if (selectedRows.length > 0 && !isPointFullySelected(d, selectedRows)) {
        return 0.3;
      }
      // alphaチャネルが指定されている場合はスケールを使用
      if (alphaScale && d.alpha != null) {
        return alphaScale(d.alpha);
      }
      return alpha;
    })
    .attr('stroke', stroke)
    .attr('stroke-width', strokeWidth);
}

/**
 * 位置調整を適用
 */
function applyPositionAdjustment(
  points: DataPoint[],
  position: PositionDef | undefined
): DataPoint[] {
  if (!position || position.type === 'identity') {
    return points;
  }

  // 位置調整は数値Xのみ対応
  const numericPoints = points as DataPoint<number>[];

  switch (position.type) {
    case 'jitter':
      return applyJitterPosition(
        numericPoints,
        position.params?.width,
        position.params?.height
      );

    case 'dodge':
      return applyDodgePosition(numericPoints, position.params?.width);

    case 'stack':
      return applyStackPosition(numericPoints);

    case 'fill':
      return applyFillPosition(numericPoints);

    default:
      return points;
  }
}

/**
 * スケールタイプに応じたD3スケールを作成（数値用）
 */
function createNumericScale(
  scaleType: ScaleType | undefined,
  domain: [number, number],
  axisDef?: AxisScaleDef
): d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number> {
  // ドメインオーバーライドを適用（ContinuousScaleDefのみ）
  let finalDomain: [number, number] = domain;
  if (isContinuousScale(axisDef) && axisDef.domain) {
    const { min, max } = axisDef.domain;
    finalDomain = [
      min !== undefined ? min : domain[0],
      max !== undefined ? max : domain[1]
    ];
  }

  let scale: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number>;

  switch (scaleType) {
    case 'log':
      // 対数スケール（ドメインは正の値のみ）
      scale = d3.scaleLog()
        .domain([
          Math.max(finalDomain[0], 0.0001), // 0や負の値を避ける
          Math.max(finalDomain[1], 0.0001)
        ]);
      break;

    case 'sqrt':
      // 平方根スケール
      scale = d3.scaleSqrt()
        .domain(finalDomain);
      break;

    case 'time':
      // 時間スケール（未実装、linearとして扱う）
      // TODO: 将来的にd3.scaleTimeを使用
      scale = d3.scaleLinear()
        .domain(finalDomain)
        .nice();
      break;

    case 'linear':
    default:
      // 線形スケール（デフォルト）
      scale = d3.scaleLinear()
        .domain(finalDomain)
        .nice();
      break;
  }

  return scale;
}

/**
 * カテゴリカルスケールを作成
 * バーグラフでの使用を想定し、paddingOuterを小さく設定して軸との重なりを防ぐ
 */
function createCategoricalScale(
  domain: string[],
  range: [number, number],
  paddingInner = 0.1
): d3.ScaleBand<string> {
  return d3.scaleBand<string>()
    .domain(domain)
    .range(range)
    .paddingInner(paddingInner)
    .paddingOuter(0.5) // 両端にパディングを追加（バーがy軸に重ならないように）
    .align(0.5); // パディングを両端に均等に配分（デフォルトだが明示的に設定）
}

/**
 * X軸とY軸のドメインを計算
 * 全レイヤーのデータポイントから、軸の範囲を決定する
 * Secondary Y軸（y2）が有効な場合は、y2Domainも計算する
 */
function calculateDomains(
  layerData: Array<{
    layer: LayerDef;
    points: DataPoint[];
    scales: {
      color?: d3.ScaleOrdinal<string | number, string>;
      fill?: d3.ScaleOrdinal<string | number, string>;
      stroke?: d3.ScaleOrdinal<string | number, string>;
      size?: d3.ScaleLinear<number, number>;
      shape?: d3.ScaleOrdinal<string | number, string>;
      alpha?: d3.ScaleLinear<number, number>;
      linetype?: d3.ScaleOrdinal<string | number, string>;
    };
  }>,
  isXCategorical: boolean,
  isYCategorical: boolean,
  hasBarGeom: boolean,
  config: CustomGraphConfig
): {
  xDomain: [number, number] | string[];
  yDomain: [number, number] | string[];
  y2Domain: [number, number] | string[] | null;
} {
  const allPoints = layerData.flatMap(({ points }) => points);
  if (allPoints.length === 0) {
    return {
      xDomain: [0, 1] as [number, number],
      yDomain: [0, 1] as [number, number],
      y2Domain: null
    };
  }

  // X軸のドメイン
  let xDomain = isXCategorical
    ? getCategoricalDomain(
        Array.from(new Set(allPoints.map(d => String(d.x)))),
        isCategoricalScale(config.scales?.x) ? config.scales.x : undefined
      )
    : (d3.extent(allPoints as DataPoint<number>[], d => d.x) as [number, number]);

  // バーグラフ使用時は、数値スケールのdomainを拡張してバーが軸に重ならないようにする
  if (!isXCategorical && hasBarGeom) {
    const [min, max] = xDomain as [number, number];
    const padding = (max - min) * 0.05;
    xDomain = [min - padding, max + padding] as [number, number];
  }

  // Primary Y軸のドメイン（yAxis='primary'のレイヤーのみ使用）
  const primaryYPoints = layerData
    .filter(({ layer }) => (layer.yAxis ?? 'primary') === 'primary')
    .flatMap(({ points }) => points);

  let yDomain = isYCategorical
    ? getCategoricalDomain(
        Array.from(new Set(primaryYPoints.map(d => String(d.y)))),
        isCategoricalScale(config.scales?.y) ? config.scales.y : undefined
      )
    : (() => {
        if (primaryYPoints.length === 0) return [0, 1] as [number, number];
        const [min, max] = d3.extent(primaryYPoints, d => d.y) as [number, number];
        // Bar geomの場合はY軸を0から始める（棒グラフの慣例）
        return (hasBarGeom ? [0, max] : [min, max]) as [number, number];
      })();

  // バーグラフ使用時は、数値スケールのdomainを拡張してバーが軸に重ならないようにする
  if (!isYCategorical && hasBarGeom) {
    const [min, max] = yDomain as [number, number];
    const padding = (max - min) * 0.05;
    // Y軸の場合は上側のみpaddingを追加（下側は0から始めるため）
    yDomain = [min, max + padding] as [number, number];
  }

  // Secondary Y軸のドメイン（y2が設定されている場合のみ）
  let y2Domain: [number, number] | string[] | null = null;
  if (config.scales?.y2) {
    const secondaryYPoints = layerData
      .filter(({ layer }) => layer.yAxis === 'secondary')
      .flatMap(({ points }) => points);

    if (secondaryYPoints.length > 0) {
      // Secondary Y軸は常に数値スケールを想定（カテゴリカルは非対応）
      const [min, max] = d3.extent(secondaryYPoints, d => d.y) as [number, number];
      y2Domain = [min, max] as [number, number];

      // paddingを追加
      const padding = (max - min) * 0.05;
      y2Domain = [min - padding, max + padding] as [number, number];
    }
  }

  return { xDomain, yDomain, y2Domain };
}

/**
 * カテゴリカルaestheticのドメインを取得（ordinalOrderを考慮）
 * Issue#276: ordinal変数のlegendが順序を考慮するよう修正
 */
function getCategoricalAesDomain(
  values: (string | number)[],
  columnId: ColumnId | undefined,
  dataset: DataSet
): (string | number)[] {
  if (columnId) {
    const column = dataset.schema.columns.find(col => col.id === columnId);
    if (column?.scale?.scale === 'ordinal' && column.scale.ordinalOrder) {
      // ordinalOrderを優先使用
      return column.scale.ordinalOrder.map(v =>
        typeof v === 'string' ? v : v as number
      ) as (string | number)[];
    }
  }
  // ordinalOrderがない場合はSetで重複除去
  return Array.from(new Set(values));
}

/**
 * Aesthetic domainsを計算（Issue#240）
 * 全パネルのStat変換後のデータから各aestheticのドメインを抽出
 * @internal テスト用にexport
 */
export function calculateAestheticDomains(
  layerData: Array<{
    layer: LayerDef;
    points: DataPoint[];
    scales: Record<string, unknown>;
  }>,
  fillScaleType: 'categorical' | 'sequential' | 'diverging',
  // Issue#276: ordinalOrderを考慮するための追加引数
  aesColumnIds?: {
    color?: ColumnId;
    fill?: ColumnId;
    stroke?: ColumnId;
    shape?: ColumnId;
    linetype?: ColumnId;
  },
  dataset?: DataSet
): {
  colorDomain?: (string | number)[];
  fillDomain?: (string | number)[] | [number, number];
  strokeDomain?: (string | number)[];
  sizeDomain?: [number, number];
  alphaDomain?: [number, number];
  shapeDomain?: (string | number)[];
  linetypeDomain?: (string | number)[];
} {
  const allPoints = layerData.flatMap(({ points }) => points);

  // colorドメイン（カテゴリカル）
  const colorValues = allPoints.map(p => p.color).filter((v): v is string | number => v != null);
  const colorDomain = colorValues.length > 0
    ? (dataset ? getCategoricalAesDomain(colorValues, aesColumnIds?.color, dataset) : Array.from(new Set(colorValues)))
    : undefined;

  // fillドメイン（カテゴリカルまたは連続）
  const fillValues = allPoints.map(p => p.fill).filter((v): v is string | number => v != null);
  let fillDomain: (string | number)[] | [number, number] | undefined;
  if (fillValues.length > 0) {
    if (fillScaleType === 'sequential' || fillScaleType === 'diverging') {
      // 連続スケール: min/maxを計算
      const numericValues = fillValues.filter((v): v is number => typeof v === 'number');
      if (numericValues.length > 0) {
        fillDomain = d3.extent(numericValues) as [number, number];
      }
    } else {
      // カテゴリカルスケール（Issue#276: ordinalOrderを考慮）
      fillDomain = dataset
        ? getCategoricalAesDomain(fillValues, aesColumnIds?.fill, dataset)
        : Array.from(new Set(fillValues));
    }
  }

  // strokeドメイン（カテゴリカル）
  const strokeValues = allPoints.map(p => p.stroke).filter((v): v is string | number => v != null);
  const strokeDomain = strokeValues.length > 0
    ? (dataset ? getCategoricalAesDomain(strokeValues, aesColumnIds?.stroke, dataset) : Array.from(new Set(strokeValues)))
    : undefined;

  // sizeドメイン（連続）
  const sizeValues = allPoints.map(p => p.size).filter((v): v is number => typeof v === 'number');
  const sizeDomain = sizeValues.length > 0
    ? d3.extent(sizeValues) as [number, number]
    : undefined;

  // alphaドメイン（連続）
  const alphaValues = allPoints.map(p => p.alpha).filter((v): v is number => typeof v === 'number');
  const alphaDomain = alphaValues.length > 0
    ? d3.extent(alphaValues) as [number, number]
    : undefined;

  // shapeドメイン（カテゴリカル）
  const shapeValues = allPoints.map(p => p.shape).filter((v): v is string | number => v != null);
  const shapeDomain = shapeValues.length > 0
    ? (dataset ? getCategoricalAesDomain(shapeValues, aesColumnIds?.shape, dataset) : Array.from(new Set(shapeValues)))
    : undefined;

  // linetypeドメイン（カテゴリカル）
  const linetypeValues = allPoints.map(p => p.linetype).filter((v): v is string | number => v != null);
  const linetypeDomain = linetypeValues.length > 0
    ? (dataset ? getCategoricalAesDomain(linetypeValues, aesColumnIds?.linetype, dataset) : Array.from(new Set(linetypeValues)))
    : undefined;

  return {
    colorDomain,
    fillDomain,
    strokeDomain,
    sizeDomain,
    alphaDomain,
    shapeDomain,
    linetypeDomain,
  };
}

/**
 * レイヤーのデータを準備
 * データポイントの抽出、統計変換、位置調整、スケール作成を行う
 */
function prepareLayerData(
  layer: LayerDef,
  aes: {
    x?: AesValue;
    y?: AesValue;
    color?: AesValue;
    fill?: AesValue;
    stroke?: AesValue;
    size?: AesValue;
    shape?: AesValue;
    alpha?: AesValue;
    linetype?: AesValue;
    group?: AesValue;
  },
  dataset: DataSet,
  config: CustomGraphConfig,
  // Facet使用時に全パネルで共通のドメインを使用
  fixedAesDomains?: {
    colorDomain?: (string | number)[];
    fillDomain?: (string | number)[] | [number, number];
    strokeDomain?: (string | number)[];
    sizeDomain?: [number, number];
    alphaDomain?: [number, number];
    shapeDomain?: (string | number)[];
    linetypeDomain?: (string | number)[];
  },
  // Issue#241: Facet complete使用時に全パネル共通のX/Y値を使用
  sharedXYValues?: {
    xValues?: unknown[];
    yValues?: unknown[];
  }
): {
  layer: LayerDef;
  points: DataPoint[];
  scales: {
    color?: d3.ScaleOrdinal<string | number, string>;
    fill?: d3.ScaleOrdinal<string | number, string>;
    stroke?: d3.ScaleOrdinal<string | number, string>;
    size?: d3.ScaleLinear<number, number>;
    shape?: d3.ScaleOrdinal<string | number, string>;
    alpha?: d3.ScaleLinear<number, number>;
    linetype?: d3.ScaleOrdinal<string | number, string>;
  };
} {
  // 各aestheticチャネルのColumnIdを取得
  const xColumnId = aes.x as ColumnId | undefined;
  const yColumnId = aes.y as ColumnId | undefined;
  const colorColumnId = aes.color as ColumnId | undefined;
  const fillColumnId = aes.fill as ColumnId | undefined;
  const strokeColumnId = aes.stroke as ColumnId | undefined;
  const sizeColumnId = aes.size as ColumnId | undefined;
  const shapeColumnId = aes.shape as ColumnId | undefined;
  const alphaColumnId = aes.alpha as ColumnId | undefined;
  const linetypeColumnId = aes.linetype as ColumnId | undefined;
  const groupColumnId = aes.group as ColumnId | undefined;

  // Stat typeを取得
  const statType = layer.stat?.type || 'identity';
  // Yがオプション（なくても動作する）Stat: count, bin, density, summary(output='y'の場合)
  const isYOptionalStat = statType === 'count' || statType === 'bin' || statType === 'density' ||
    (statType === 'summary' && layer.stat?.output === 'y');

  // X は常に必須、Y は YOptionalStat 以外で必須
  if (!xColumnId) {
    return { layer, points: [], scales: {} };
  }
  if (!isYOptionalStat && !yColumnId) {
    return { layer, points: [], scales: {} };
  }

  // 各チャネルのデータを取得
  const xData = dataset.data?.[xColumnId];
  const yData = yColumnId ? dataset.data?.[yColumnId] : undefined;
  const colorData = colorColumnId ? dataset.data?.[colorColumnId] : undefined;
  const fillData = fillColumnId ? dataset.data?.[fillColumnId] : undefined;
  const strokeData = strokeColumnId ? dataset.data?.[strokeColumnId] : undefined;
  const sizeData = sizeColumnId ? dataset.data?.[sizeColumnId] : undefined;
  const shapeData = shapeColumnId ? dataset.data?.[shapeColumnId] : undefined;
  const alphaData = alphaColumnId ? dataset.data?.[alphaColumnId] : undefined;
  const linetypeData = linetypeColumnId ? dataset.data?.[linetypeColumnId] : undefined;
  const groupData = groupColumnId ? dataset.data?.[groupColumnId] : undefined;

  // 有効なデータポイントの抽出
  let points: DataPoint[] = [];

  if (isYOptionalStat) {
    // Y optional stat: x のみ必要、x は任意の型（Countの場合）または数値（Bin/Densityの場合）
    for (let i = 0; i < (dataset.rowCount || 0); i++) {
      const x = xData?.[i];
      if (x != null) {
        // x を数値または文字列として扱う
        const xValue = typeof x === 'number' ? x : String(x);
        points.push({
          x: xValue,
          y: NaN, // ダミー値（Yなし）。stat変換で上書きされる
          index: i,
          color: colorData?.[i] as string | number | undefined,
          fill: fillData?.[i] as string | number | undefined,
          stroke: strokeData?.[i] as string | number | undefined,
          size: sizeData?.[i] as number | undefined,
          shape: shapeData?.[i] as string | number | undefined,
          alpha: alphaData?.[i] as number | undefined,
          linetype: linetypeData?.[i] as string | number | undefined,
          group: groupData?.[i] as string | number | undefined,
          category: colorData?.[i] as string | number | undefined,
        });
      }
    }
  } else {
    // Identity/Smooth stat: x, y 両方必要
    // Issue#243: Tile geomの場合はカテゴリカル（文字列）も許可
    const isTileGeom = layer.geom.type === 'tile';
    for (let i = 0; i < (dataset.rowCount || 0); i++) {
      const x = xData?.[i];
      const y = yData?.[i];
      // Tile geomの場合は数値・文字列どちらも許可、それ以外は数値のみ
      const isValidX = x != null && (isTileGeom || typeof x === 'number');
      const isValidY = y != null && (isTileGeom || typeof y === 'number');
      if (isValidX && isValidY) {
        // Tile geomで文字列の場合はそのまま文字列として保持
        // 型アサーションで対応（実行時には問題なく動作する）
        const xValue = typeof x === 'number' ? x : String(x);
        const yValue = typeof y === 'number' ? y : String(y);
        points.push({
          x: xValue,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          y: yValue as any, // Tile geomでは文字列の場合もある（描画ロジックで対応済み）
          index: i,
          color: colorData?.[i] as string | number | undefined,
          fill: fillData?.[i] as string | number | undefined,
          stroke: strokeData?.[i] as string | number | undefined,
          size: sizeData?.[i] as number | undefined,
          shape: shapeData?.[i] as string | number | undefined,
          alpha: alphaData?.[i] as number | undefined,
          linetype: linetypeData?.[i] as string | number | undefined,
          group: groupData?.[i] as string | number | undefined,
          category: colorData?.[i] as string | number | undefined,
        });
      }
    }
  }

  // 統計変換を適用
  // Issue#241: sharedXYValuesがある場合はFacet completeを使用
  points = applyStatTransform(points, layer.stat, sharedXYValues);

  // 位置調整を適用
  points = applyPositionAdjustment(points, layer.position);

  // 各チャネルのスケールを構築
  const scales: {
    color?: d3.ScaleOrdinal<string | number, string>;
    fill?: d3.ScaleOrdinal<string | number, string>;
    fillContinuous?: d3.ScaleSequential<string> | d3.ScaleDiverging<string>;
    stroke?: d3.ScaleOrdinal<string | number, string>;
    size?: d3.ScaleLinear<number, number>;
    shape?: d3.ScaleOrdinal<string | number, string>;
    alpha?: d3.ScaleLinear<number, number>;
    linetype?: d3.ScaleOrdinal<string | number, string>;
  } = {};

  // colorスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  if (fixedAesDomains?.colorDomain && fixedAesDomains.colorDomain.length > 0) {
    const paletteId = config.scales?.color?.paletteId || DEFAULT_PALETTE_IDS.categorical;
    scales.color = createColorScale(paletteId, fixedAesDomains.colorDomain.map(String));
  } else if (colorData) {
    // Issue#276: ordinalOrderを考慮
    const colorColumnId = typeof aes.color === 'string' ? aes.color as ColumnId : undefined;
    const uniqueValues = getCategoricalAesDomain(
      colorData.filter((v): v is string | number => v != null),
      colorColumnId,
      dataset
    );
    if (uniqueValues.length > 0) {
      const paletteId = config.scales?.color?.paletteId || DEFAULT_PALETTE_IDS.categorical;
      scales.color = createColorScale(paletteId, uniqueValues.map(String));
    }
  }

  // 統計変換後のデータからcolorスケールを作成または更新
  // (density等の統計変換がデータポイントにcolorプロパティを追加する場合に対応)
  // fixedAesDomainsがある場合は既に作成済み
  const colorValuesAfterStat = Array.from(new Set(points.map(p => p.color).filter((v): v is string | number => v != null)));
  if (colorValuesAfterStat.length > 0 && !scales.color) {
    const paletteId = config.scales?.color?.paletteId || DEFAULT_PALETTE_IDS.categorical;
    scales.color = createColorScale(paletteId, colorValuesAfterStat.map(String));
  }

  // fillスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  const fillScaleType = config.scales?.fill?.scaleType || 'categorical';
  if (fixedAesDomains?.fillDomain && fixedAesDomains.fillDomain.length > 0) {
    // fixedAesDomainsがある場合
    if (fillScaleType === 'sequential' || fillScaleType === 'diverging') {
      // 連続色スケール: fillDomainは[min, max]
      const [minVal, maxVal] = fixedAesDomains.fillDomain as [number, number];
      if (fillScaleType === 'sequential') {
        const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.sequential;
        scales.fillContinuous = createSequentialScale(paletteId, [minVal, maxVal]);
      } else {
        const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.diverging;
        scales.fillContinuous = createDivergingScale(paletteId, [minVal, maxVal]);
      }
    } else {
      // カテゴリカル色スケール: fillDomainはuniqueValues配列
      const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.categorical;
      scales.fill = createColorScale(paletteId, fixedAesDomains.fillDomain.map(String));
    }
  } else if (fillData) {
    if (fillScaleType === 'sequential' || fillScaleType === 'diverging') {
      // 連続色スケール（数値データ用）
      const numericValues = fillData.filter((v): v is number => typeof v === 'number');
      if (numericValues.length > 0) {
        const domain = config.scales?.fill?.domain;
        const minVal = domain?.min ?? Math.min(...numericValues);
        const maxVal = domain?.max ?? Math.max(...numericValues);

        if (fillScaleType === 'sequential') {
          const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.sequential;
          scales.fillContinuous = createSequentialScale(paletteId, [minVal, maxVal]);
        } else {
          const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.diverging;
          scales.fillContinuous = createDivergingScale(paletteId, [minVal, maxVal]);
        }
      }
    } else {
      // カテゴリカル色スケール（Issue#276: ordinalOrderを考慮）
      const fillColumnId = typeof aes.fill === 'string' ? aes.fill as ColumnId : undefined;
      const uniqueValues = getCategoricalAesDomain(
        fillData.filter((v): v is string | number => v != null),
        fillColumnId,
        dataset
      );
      if (uniqueValues.length > 0) {
        const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.categorical;
        scales.fill = createColorScale(paletteId, uniqueValues.map(String));
      }
    }
  }

  // 統計変換後のデータからfillスケールを作成または更新
  // fixedAesDomainsがある場合は既に作成済み
  const fillValuesAfterStat = Array.from(new Set(points.map(p => p.fill).filter((v): v is string | number => v != null)));
  if (fillValuesAfterStat.length > 0 && !scales.fill && !scales.fillContinuous) {
    if (fillScaleType === 'sequential' || fillScaleType === 'diverging') {
      // 連続色スケール
      const numericValues = fillValuesAfterStat.filter((v): v is number => typeof v === 'number');
      if (numericValues.length > 0) {
        const domain = config.scales?.fill?.domain;
        const minVal = domain?.min ?? Math.min(...numericValues);
        const maxVal = domain?.max ?? Math.max(...numericValues);

        if (fillScaleType === 'sequential') {
          const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.sequential;
          scales.fillContinuous = createSequentialScale(paletteId, [minVal, maxVal]);
        } else {
          const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.diverging;
          scales.fillContinuous = createDivergingScale(paletteId, [minVal, maxVal]);
        }
      }
    } else {
      const paletteId = config.scales?.fill?.paletteId || DEFAULT_PALETTE_IDS.categorical;
      scales.fill = createColorScale(paletteId, fillValuesAfterStat.map(String));
    }
  }

  // strokeスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  if (fixedAesDomains?.strokeDomain && fixedAesDomains.strokeDomain.length > 0) {
    const paletteId = config.scales?.stroke?.paletteId || DEFAULT_PALETTE_IDS.categorical;
    scales.stroke = createColorScale(paletteId, fixedAesDomains.strokeDomain.map(String));
  } else if (strokeData) {
    // Issue#276: ordinalOrderを考慮
    const strokeColumnId = typeof aes.stroke === 'string' ? aes.stroke as ColumnId : undefined;
    const uniqueValues = getCategoricalAesDomain(
      strokeData.filter((v): v is string | number => v != null),
      strokeColumnId,
      dataset
    );
    if (uniqueValues.length > 0) {
      const paletteId = config.scales?.stroke?.paletteId || DEFAULT_PALETTE_IDS.categorical;
      scales.stroke = createColorScale(paletteId, uniqueValues.map(String));
    }
  }

  // 統計変換後のデータからstrokeスケールを作成または更新
  // fixedAesDomainsがある場合は既に作成済み
  const strokeValuesAfterStat = Array.from(new Set(points.map(p => p.stroke).filter((v): v is string | number => v != null)));
  if (strokeValuesAfterStat.length > 0 && !scales.stroke) {
    const paletteId = config.scales?.stroke?.paletteId || DEFAULT_PALETTE_IDS.categorical;
    scales.stroke = createColorScale(paletteId, strokeValuesAfterStat.map(String));
  }

  // sizeスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  const sizeRange = [
    config.scales?.size?.range?.min ?? 2,
    config.scales?.size?.range?.max ?? 20
  ] as [number, number];
  if (fixedAesDomains?.sizeDomain) {
    scales.size = d3.scaleLinear().domain(fixedAesDomains.sizeDomain).range(sizeRange);
  } else if (sizeData) {
    const numericValues = sizeData.filter((v): v is number => typeof v === 'number');
    if (numericValues.length > 0) {
      const domain = d3.extent(numericValues) as [number, number];
      scales.size = d3.scaleLinear().domain(domain).range(sizeRange);
    }
  }

  // shapeスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  const shapes = config.scales?.shape?.shapes || ['circle', 'square', 'triangle', 'diamond', 'cross', 'plus'];
  if (fixedAesDomains?.shapeDomain && fixedAesDomains.shapeDomain.length > 0) {
    scales.shape = d3.scaleOrdinal<string | number, string>()
      .domain(fixedAesDomains.shapeDomain)
      .range(shapes);
  } else if (shapeData) {
    // Issue#276: ordinalOrderを考慮
    const shapeColumnId = typeof aes.shape === 'string' ? aes.shape as ColumnId : undefined;
    const uniqueValues = getCategoricalAesDomain(
      shapeData.filter((v): v is string | number => v != null),
      shapeColumnId,
      dataset
    );
    if (uniqueValues.length > 0) {
      scales.shape = d3.scaleOrdinal<string | number, string>()
        .domain(uniqueValues)
        .range(shapes);
    }
  }

  // 統計変換後のデータからshapeスケールを作成または更新
  // fixedAesDomainsがある場合は既に作成済み
  const shapeValuesAfterStat = Array.from(new Set(points.map(p => p.shape).filter((v): v is string | number => v != null)));
  if (shapeValuesAfterStat.length > 0 && !scales.shape) {
    scales.shape = d3.scaleOrdinal<string | number, string>()
      .domain(shapeValuesAfterStat)
      .range(shapes);
  }

  // alphaスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  const alphaRange = [
    config.scales?.alpha?.range?.min ?? 0.2,
    config.scales?.alpha?.range?.max ?? 1.0
  ] as [number, number];
  if (fixedAesDomains?.alphaDomain) {
    scales.alpha = d3.scaleLinear().domain(fixedAesDomains.alphaDomain).range(alphaRange);
  } else if (alphaData) {
    const numericValues = alphaData.filter((v): v is number => typeof v === 'number');
    if (numericValues.length > 0) {
      const domain = d3.extent(numericValues) as [number, number];
      scales.alpha = d3.scaleLinear().domain(domain).range(alphaRange);
    }
  }

  // linetypeスケール
  // fixedAesDomainsがあれば優先して使用（Facet使用時の全パネル統一）
  const linetypes = config.scales?.linetype?.linetypes || ['solid', 'dashed', 'dotted'];
  if (fixedAesDomains?.linetypeDomain && fixedAesDomains.linetypeDomain.length > 0) {
    scales.linetype = d3.scaleOrdinal<string | number, string>()
      .domain(fixedAesDomains.linetypeDomain)
      .range(linetypes);
  } else if (linetypeData) {
    // Issue#276: ordinalOrderを考慮
    const linetypeColumnId = typeof aes.linetype === 'string' ? aes.linetype as ColumnId : undefined;
    const uniqueValues = getCategoricalAesDomain(
      linetypeData.filter((v): v is string | number => v != null),
      linetypeColumnId,
      dataset
    );
    if (uniqueValues.length > 0) {
      scales.linetype = d3.scaleOrdinal<string | number, string>()
        .domain(uniqueValues)
        .range(linetypes);
    }
  }

  return { layer, points, scales };
}

interface CustomGraphRendererProps {
  config: CustomGraphConfig;
  dataset: DataSet;
  selectedRows?: number[];
  onPointClick?: (rowIndices: number[], isAdditive: boolean) => void;
}

/**
 * Custom Graph Renderer
 * Grammar of Graphicsに基づいてグラフを描画
 */
export function CustomGraphRenderer({
  config,
  dataset,
  selectedRows = [],
  onPointClick,
}: CustomGraphRendererProps) {
  // 表示可能なレイヤーを取得
  const visibleLayers = config.layers.filter(layer => layer.visible !== false);

  if (visibleLayers.length === 0) {
    return <div style={{ padding: '20px', color: '#666' }}>No visible layers</div>;
  }

  // 各レイヤーのaestheticマッピングを解決
  const layersWithResolvedAes = visibleLayers.map(layer => ({
    layer,
    aes: {
      ...config.globalAes,
      ...layer.aes,
    },
  }));

  // 少なくとも1つのレイヤーにx, yが必要（ただしCount/Bin/Density statの場合はyは不要）
  const hasValidAes = layersWithResolvedAes.some(({ layer, aes }) => {
    const statType = layer.stat?.type || 'identity';
    const isCountOrBinStat = statType === 'count' || statType === 'bin';
    const isDensityStat = statType === 'density';
    // X は常に必須
    if (!aes.x) return false;
    // Y は Count/Bin/Density 以外の stat で必須
    // Count/Bin/DensityはY値を自動生成する
    if (!isCountOrBinStat && !isDensityStat && !aes.y) return false;
    return true;
  });
  if (!hasValidAes) {
    return <div style={{ padding: '20px', color: '#666' }}>At least one layer must have X and Y aesthetics</div>;
  }

  // ファセット機能が有効な場合
  if (config.facets) {
    return (
      <FacetedGraphRenderer
        config={config}
        dataset={dataset}
        layersWithAes={layersWithResolvedAes}
        selectedRows={selectedRows}
        onPointClick={onPointClick}
      />
    );
  }

  // 通常のグラフレンダリング
  return (
    <MultiLayerRenderer
      config={config}
      dataset={dataset}
      layersWithAes={layersWithResolvedAes}
      selectedRows={selectedRows}
      onPointClick={onPointClick}
    />
  );
}

/**
 * Faceted Graph Renderer
 * ファセット機能を実装したグラフレンダラー
 */
interface FacetedGraphRendererProps {
  config: CustomGraphConfig;
  dataset: DataSet;
  layersWithAes: Array<{
    layer: LayerDef;
    aes: {
      x?: AesValue;
      y?: AesValue;
      color?: AesValue;
      fill?: AesValue;
      stroke?: AesValue;
      size?: AesValue;
      shape?: AesValue;
      alpha?: AesValue;
      linetype?: AesValue;
    };
  }>;
  selectedRows: number[];
  onPointClick?: (rowIndices: number[], isAdditive: boolean) => void;
}

function FacetedGraphRenderer({
  config,
  dataset,
  layersWithAes,
  selectedRows,
  onPointClick,
}: FacetedGraphRendererProps) {
  const facets = config.facets!; // 呼び出し元でチェック済み

  // 親コンテナのサイズを監視
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    if (!containerRef.current) return;

    // 初期高さを即座に設定
    setContainerHeight(containerRef.current.clientHeight);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // ファセット変数のユニークな値を取得
  const facetData = React.useMemo(() => {
    if (!dataset.data || !dataset.rowCount) {
      return { panels: [], nrow: 0, ncol: 0 };
    }

    if (facets.type === 'wrap') {
      // Facet Wrap: 1つの変数で分割
      const column = dataset.schema.columns.find(c => c.id === facets.variable);
      if (!column) {
        return { panels: [], nrow: 0, ncol: 0 };
      }

      const facetColumnData = dataset.data[column.id];
      if (!facetColumnData) {
        return { panels: [], nrow: 0, ncol: 0 };
      }

      // ユニークな値を取得（自然順ソート）
      const uniqueValues = Array.from(new Set(facetColumnData)).sort((a, b) => {
        if (typeof a === 'string' && typeof b === 'string') {
          return a.localeCompare(b);
        }
        return String(a).localeCompare(String(b));
      });

      // グリッドレイアウトを計算
      const nPanels = uniqueValues.length;
      let nrow = facets.nrow;
      let ncol = facets.ncol;

      if (!nrow && !ncol) {
        // デフォルト: できるだけ正方形に近いレイアウト
        ncol = Math.ceil(Math.sqrt(nPanels));
        nrow = Math.ceil(nPanels / ncol);
      } else if (nrow && !ncol) {
        ncol = Math.ceil(nPanels / nrow);
      } else if (!nrow && ncol) {
        nrow = Math.ceil(nPanels / ncol);
      }

      // パネル情報を生成
      const panels = uniqueValues.map(value => {
        // データをフィルタリング（columnar format）
        const filteredIndices: number[] = [];
        for (let i = 0; i < dataset.rowCount!; i++) {
          if (facetColumnData[i] === value) {
            filteredIndices.push(i);
          }
        }

        // 新しいデータセットを作成（columnar format）
        const filteredData: Record<ColumnId, DataValue[]> = {};
        for (const col of dataset.schema.columns) {
          const columnData = dataset.data![col.id];
          if (columnData) {
            filteredData[col.id] = filteredIndices.map(idx => columnData[idx]);
          }
        }

        // 選択行のインデックスをフィルタリング後のインデックスに変換
        const mappedSelectedRows = selectedRows
          .map(originalIdx => filteredIndices.indexOf(originalIdx))
          .filter(idx => idx !== -1);

        return {
          title: `${column.name} = ${value}`,
          dataset: {
            ...dataset,
            data: filteredData,
            rowCount: filteredIndices.length,
          },
          selectedRows: mappedSelectedRows,
          originalIndices: filteredIndices,
        };
      });

      return { panels, nrow: nrow!, ncol: ncol! };
    } else {
      // Facet Grid: 2つの変数で行・列に分割
      const rowsColumn = facets.rows ? dataset.schema.columns.find(c => c.id === facets.rows) : undefined;
      const colsColumn = facets.cols ? dataset.schema.columns.find(c => c.id === facets.cols) : undefined;

      if (!rowsColumn && !colsColumn) {
        return { panels: [], nrow: 0, ncol: 0 };
      }

      const rowsColumnData = rowsColumn ? dataset.data[rowsColumn.id] : undefined;
      const colsColumnData = colsColumn ? dataset.data[colsColumn.id] : undefined;

      // 行方向の値
      const rowValues = rowsColumnData
        ? Array.from(new Set(rowsColumnData)).sort((a, b) => String(a).localeCompare(String(b)))
        : [null];

      // 列方向の値
      const colValues = colsColumnData
        ? Array.from(new Set(colsColumnData)).sort((a, b) => String(a).localeCompare(String(b)))
        : [null];

      // パネル情報を生成
      const panels: Array<{
        title: string;
        dataset: DataSet;
        selectedRows: number[];
        originalIndices: number[];
      }> = [];

      for (const rowValue of rowValues) {
        for (const colValue of colValues) {
          // データをフィルタリング（columnar format）
          const filteredIndices: number[] = [];
          for (let i = 0; i < dataset.rowCount!; i++) {
            const rowMatch = !rowsColumnData || rowsColumnData[i] === rowValue;
            const colMatch = !colsColumnData || colsColumnData[i] === colValue;
            if (rowMatch && colMatch) {
              filteredIndices.push(i);
            }
          }

          // 新しいデータセットを作成（columnar format）
          const filteredData: Record<ColumnId, DataValue[]> = {};
          for (const col of dataset.schema.columns) {
            const columnData = dataset.data![col.id];
            if (columnData) {
              filteredData[col.id] = filteredIndices.map(idx => columnData[idx]);
            }
          }

          // タイトル生成
          const titleParts: string[] = [];
          if (rowsColumn) titleParts.push(`${rowsColumn.name} = ${rowValue}`);
          if (colsColumn) titleParts.push(`${colsColumn.name} = ${colValue}`);
          const title = titleParts.join(', ');

          // 選択行のインデックスをフィルタリング後のインデックスに変換
          const mappedSelectedRows = selectedRows
            .map(originalIdx => filteredIndices.indexOf(originalIdx))
            .filter(idx => idx !== -1);

          panels.push({
            title,
            dataset: {
              ...dataset,
              data: filteredData,
              rowCount: filteredIndices.length,
            },
            selectedRows: mappedSelectedRows,
            originalIndices: filteredIndices,
          });
        }
      }

      return { panels, nrow: rowValues.length, ncol: colValues.length };
    }
  }, [facets, dataset, selectedRows]);

  const { panels, ncol } = facetData;

  // 全パネル共通のドメインを計算（fixed scales）
  // 各パネルのデータでStat変換を実行し、全パネルで同じ軸スケール・aestheticスケールを使用
  const fixedDomains = React.useMemo(() => {
    if (panels.length === 0) return null;

    // geom_tileの場合は強制的にカテゴリカル
    const hasTileGeom = config.layers.some(l => l.geom.type === 'tile');

    // 各パネルのデータでprepareLayerDataを実行（Stat変換を含む）
    // Issue#240: Stat変換後のaesthetic domainsも全パネルで統一するため
    const allPanelLayerData = panels.flatMap(panel =>
      layersWithAes.map(({ layer, aes }) =>
        prepareLayerData(layer, aes, panel.dataset, config)
      )
    );

    // X/Yがカテゴリカルかどうかを判定（最初のレイヤーから）
    const firstLayerAes = layersWithAes[0]?.aes;
    const xColumnId = firstLayerAes?.x as ColumnId | undefined;
    const yColumnId = firstLayerAes?.y as ColumnId | undefined;
    const xColumn = xColumnId ? dataset.schema.columns.find(c => c.id === xColumnId) : undefined;
    const yColumn = yColumnId ? dataset.schema.columns.find(c => c.id === yColumnId) : undefined;

    const firstLayer = layersWithAes[0]?.layer;
    const statType = firstLayer?.stat?.type || 'identity';
    const isCountOrBinStat = statType === 'count' || statType === 'bin';

    const isXCategorical = hasTileGeom || (xColumn
      ? (xColumn.scale?.scale === 'nominal' || xColumn.scale?.scale === 'ordinal') ||
        (!xColumn.scale && (xColumn.type === 'string' || xColumn.type === 'boolean'))
      : false);

    const isYCategorical = hasTileGeom || (!isCountOrBinStat && (yColumn
      ? (yColumn.scale?.scale === 'nominal' || yColumn.scale?.scale === 'ordinal') ||
        (!yColumn.scale && (yColumn.type === 'string' || yColumn.type === 'boolean'))
      : false));

    const hasBarGeom = layersWithAes.some(({ layer }) => layer.geom.type === 'bar');
    const xyDomains = calculateDomains(allPanelLayerData, isXCategorical, isYCategorical, hasBarGeom, config);

    // Issue#240: Aesthetic domainsを計算（全パネルのStat変換後のデータから）
    // Issue#276: ordinalOrderを考慮するためaesthetic列ID情報とdatasetを渡す
    const fillScaleType = config.scales?.fill?.scaleType || 'categorical';
    const aesColumnIds = {
      color: firstLayerAes?.color as ColumnId | undefined,
      fill: firstLayerAes?.fill as ColumnId | undefined,
      stroke: firstLayerAes?.stroke as ColumnId | undefined,
      shape: firstLayerAes?.shape as ColumnId | undefined,
      linetype: firstLayerAes?.linetype as ColumnId | undefined,
    };
    const aesDomains = calculateAestheticDomains(allPanelLayerData, fillScaleType, aesColumnIds, dataset);

    return { ...xyDomains, ...aesDomains };
  }, [panels, config, layersWithAes, dataset]);

  // Issue#241: Facet使用時の共有X/Y値計算とStatistics-side complete無効化
  // Facet使用時は必ずオブジェクトを返す（Statistics-side completeを無効化するため）
  // facets.complete=trueの場合のみ実際のX/Y値を設定
  const sharedXYValues = React.useMemo(() => {
    const firstLayerAes = layersWithAes[0]?.aes;
    const xColumnId = firstLayerAes?.x as ColumnId | undefined;
    const yColumnId = firstLayerAes?.y as ColumnId | undefined;

    // facets.complete=falseの場合: Facetコンテキストを示す空オブジェクト
    // これによりStatistics-side completeが無効化される
    if (!facets.complete) {
      return { xValues: undefined, yValues: undefined };
    }

    if (!xColumnId) {
      return { xValues: undefined, yValues: undefined };
    }

    // 元データセット（Facet分割前）からX/Y値を収集
    const xData = dataset.data?.[xColumnId];
    if (!xData) {
      return { xValues: undefined, yValues: undefined };
    }

    const xValues = Array.from(new Set(xData.filter(v => v != null)));

    // Yが指定されている場合は2D補完、なければ1D補完
    let yValues: unknown[] | undefined;
    if (yColumnId) {
      const yData = dataset.data?.[yColumnId];
      if (yData) {
        yValues = Array.from(new Set(yData.filter(v => v != null)));
      }
    }

    // Yがない場合（1D補完）は空配列を渡す（completeXYGridでの識別用）
    return {
      xValues,
      yValues: yValues ?? [],
    };
  }, [facets.complete, layersWithAes, dataset]);

  if (panels.length === 0) {
    return <div style={{ padding: '20px', color: '#666' }}>No facet data available</div>;
  }

  // パネルサイズをレスポンシブに計算（コンテナサイズに応じて自動調整）
  const minPanelWidth = 400;  // 最小幅
  const minPanelHeight = 350; // 最小高さ（フォールバック）
  const titleHeight = 30;     // タイトルバーの高さ
  const gridPadding = 20;     // グリッドのpadding
  const gridGap = 20;         // グリッドのgap

  // 動的にパネルの高さを計算
  let panelHeight = minPanelHeight;
  if (containerHeight) {
    const nrow = Math.ceil(panels.length / ncol);
    const availableHeight = containerHeight - (gridPadding * 2) - (gridGap * (nrow - 1));
    const calculatedHeight = Math.floor(availableHeight / nrow);
    panelHeight = Math.max(minPanelHeight, calculatedHeight);
  }
  const graphHeight = panelHeight - titleHeight;

  return (
    <div ref={containerRef} style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${ncol}, minmax(${minPanelWidth}px, 1fr))`,
      gridAutoRows: `${panelHeight}px`,
      gap: '20px',
      padding: '20px',
      width: '100%',
      height: '100%',
    }}>
      {panels.map((panel, idx) => (
        <div key={idx} style={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          minHeight: `${minPanelHeight}px`,
        }}>
          {/* パネルタイトル */}
          <div style={{
            padding: '8px',
            background: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#666',
            flexShrink: 0,
          }}>
            {panel.title}
          </div>
          {/* グラフ */}
          <div style={{
            height: `${graphHeight}px`,
            paddingBottom: '8px', // X軸ラベルが切れないように下部にパディングを追加
          }}>
            <MultiLayerRenderer
              config={config}
              dataset={panel.dataset}
              layersWithAes={layersWithAes}
              selectedRows={panel.selectedRows}
              onPointClick={(rowIndices, isAdditive) => {
                // フィルタリング後のインデックスを元のインデックスに変換
                const originalIndices = rowIndices.map(idx => panel.originalIndices[idx]);
                onPointClick?.(originalIndices, isAdditive);
              }}
              fixedDomains={fixedDomains ?? undefined}
              sharedXYValues={sharedXYValues}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

interface MultiLayerRendererProps {
  config: CustomGraphConfig;
  dataset: DataSet;
  layersWithAes: Array<{
    layer: LayerDef;
    aes: {
      x?: AesValue;
      y?: AesValue;
      color?: AesValue;
      fill?: AesValue;
      stroke?: AesValue;
      size?: AesValue;
      shape?: AesValue;
      alpha?: AesValue;
      linetype?: AesValue;
    };
  }>;
  selectedRows: number[];
  onPointClick?: (rowIndices: number[], isAdditive: boolean) => void;
  // Facet使用時に全パネルで共通のドメインを使用するためのオプション
  fixedDomains?: {
    xDomain: [number, number] | string[];
    yDomain: [number, number] | string[];
    y2Domain?: [number, number] | string[] | null;
    // Aesthetic scales domains（Issue#240: 全パネルで統一）
    colorDomain?: (string | number)[];
    fillDomain?: (string | number)[] | [number, number];  // categorical or continuous
    strokeDomain?: (string | number)[];
    sizeDomain?: [number, number];
    alphaDomain?: [number, number];
    shapeDomain?: (string | number)[];
    linetypeDomain?: (string | number)[];
  };
  // Issue#241: Facet complete使用時に全パネル共通のX/Y値を使用
  sharedXYValues?: {
    xValues?: unknown[];
    yValues?: unknown[];
  };
}

function MultiLayerRenderer({
  config,
  dataset,
  layersWithAes,
  selectedRows,
  onPointClick,
  fixedDomains,
  sharedXYValues,
}: MultiLayerRendererProps) {
  // fixedDomainsからaesthetic domainsを抽出（Issue#240: Facet使用時の全パネル統一）
  const fixedAesDomains = React.useMemo(() => {
    if (!fixedDomains) return undefined;
    return {
      colorDomain: fixedDomains.colorDomain,
      fillDomain: fixedDomains.fillDomain,
      strokeDomain: fixedDomains.strokeDomain,
      sizeDomain: fixedDomains.sizeDomain,
      alphaDomain: fixedDomains.alphaDomain,
      shapeDomain: fixedDomains.shapeDomain,
      linetypeDomain: fixedDomains.linetypeDomain,
    };
  }, [fixedDomains]);

  // 各レイヤーのデータを準備
  // Issue#241: sharedXYValuesがある場合はFacet completeを使用
  const layerData = React.useMemo(() => {
    return layersWithAes.map(({ layer, aes }) =>
      prepareLayerData(layer, aes, dataset, config, fixedAesDomains, sharedXYValues)
    );
  }, [layersWithAes, dataset, config, fixedAesDomains, sharedXYValues]);

  // geom_tileが含まれるかどうかを判定
  const hasTileGeom = config.layers.some(l => l.geom.type === 'tile');

  // X軸とY軸がカテゴリカルかどうかを判定
  const { isXCategorical, isYCategorical } = React.useMemo(() => {
    // geom_tileの場合は強制的にカテゴリカル（バンドスケール）として扱う
    // 設計意図:
    // - geom_tileはタイルが隙間なく並ぶ必要がある
    // - バンドスケール: 各カテゴリに等幅の帯を割り当て、bandwidth()でタイル幅を取得可能
    // - リニアスケール: 連続値の「点」に対してタイルを描くため、端が切れる問題が発生
    // - int64列（週番号、月など）もString化してカテゴリとして扱うことで、タイルが隙間なく並ぶ
    if (hasTileGeom) {
      return { isXCategorical: true, isYCategorical: true };
    }

    // 最初のレイヤーのaestheticマッピングからカラムIDを取得
    const firstLayerAes = layersWithAes[0]?.aes;
    if (!firstLayerAes) {
      return { isXCategorical: false, isYCategorical: false };
    }

    const xColumnId = firstLayerAes.x as ColumnId | undefined;
    const yColumnId = firstLayerAes.y as ColumnId | undefined;

    const xColumn = xColumnId ? dataset.schema.columns.find(c => c.id === xColumnId) : undefined;
    const yColumn = yColumnId ? dataset.schema.columns.find(c => c.id === yColumnId) : undefined;

    // 統計変換のタイプを取得
    const firstLayer = layersWithAes[0]?.layer;
    const statType = firstLayer?.stat?.type || 'identity';
    const isCountOrBinStat = statType === 'count' || statType === 'bin';

    // カラムがカテゴリカルかどうかを判定
    // 1. scaleが指定されている場合はそれを使用
    // 2. scaleが指定されていない場合はtypeから推測（string, booleanは categorical）
    const isXCat = xColumn
      ? (xColumn.scale?.scale === 'nominal' || xColumn.scale?.scale === 'ordinal') ||
        (!xColumn.scale && (xColumn.type === 'string' || xColumn.type === 'boolean'))
      : false;

    const isYCat = yColumn
      ? (yColumn.scale?.scale === 'nominal' || yColumn.scale?.scale === 'ordinal') ||
        (!yColumn.scale && (yColumn.type === 'string' || yColumn.type === 'boolean'))
      : false;

    const result = {
      isXCategorical: isXCat,
      // Count/Bin統計の場合、Y軸は常に数値（カウント数）になる
      isYCategorical: isCountOrBinStat ? false : isYCat,
    };

    return result;
  }, [layersWithAes, dataset.schema.columns, hasTileGeom]);

  // 軸タイトルの自動生成（titleが未設定の場合に使用）
  const autoAxisTitles = React.useMemo(() => {
    const firstLayerAes = layersWithAes[0]?.aes;
    if (!firstLayerAes) {
      return { xTitle: undefined, yTitle: undefined };
    }

    const xColumnId = firstLayerAes.x as ColumnId | undefined;
    const yColumnId = firstLayerAes.y as ColumnId | undefined;

    const xColumn = xColumnId ? dataset.schema.columns.find(c => c.id === xColumnId) : undefined;
    const yColumn = yColumnId ? dataset.schema.columns.find(c => c.id === yColumnId) : undefined;

    // 統計変換のタイプを取得
    const firstLayer = layersWithAes[0]?.layer;
    const statType = firstLayer?.stat?.type || 'identity';

    // X軸タイトル: カラム名をそのまま使用
    const xTitle = xColumn?.name;

    // Y軸タイトル: Count/Bin/Density統計の場合は固定値、それ以外はカラム名
    let yTitle: string | undefined;
    if (statType === 'count') {
      yTitle = 'Count';
    } else if (statType === 'bin') {
      // Bin統計のyScaleパラメータを確認
      const binStat = firstLayer?.stat as { type: 'bin'; params?: { yScale?: 'count' | 'density' } } | undefined;
      const yScale = binStat?.params?.yScale || 'count';
      yTitle = yScale === 'density' ? 'Density' : 'Count';
    } else if (statType === 'density') {
      // Density統計のyScaleパラメータを確認
      const densityStat = firstLayer?.stat as { type: 'density'; params?: { yScale?: 'count' | 'density' } } | undefined;
      const yScale = densityStat?.params?.yScale || 'density';
      yTitle = yScale === 'density' ? 'Density' : 'Count';
    } else {
      yTitle = yColumn?.name;
    }

    return { xTitle, yTitle };
  }, [layersWithAes, dataset.schema.columns]);

  // 全レイヤーのポイントからドメインを計算
  // fixedDomainsが指定されている場合（Facet使用時）はそれを使用
  const { xDomain, yDomain, y2Domain } = React.useMemo<{
    xDomain: [number, number] | string[];
    yDomain: [number, number] | string[];
    y2Domain: [number, number] | string[] | null;
  }>(() => {
    if (fixedDomains) {
      return {
        xDomain: fixedDomains.xDomain,
        yDomain: fixedDomains.yDomain,
        y2Domain: fixedDomains.y2Domain ?? null,
      };
    }
    const hasBarGeom = layersWithAes.some(({ layer }) => layer.geom.type === 'bar');
    return calculateDomains(layerData, isXCategorical, isYCategorical, hasBarGeom, config);
  }, [layerData, isXCategorical, isYCategorical, layersWithAes, config, fixedDomains]);

  // ズーム設定を計算
  const zoomOptions = React.useMemo(() => {
    // カテゴリカル軸がある場合はズーム無効化
    const hasCategorial = isXCategorical || isYCategorical;
    if (hasCategorial) {
      return undefined; // enableZoom: falseにするため
    }

    // 数値軸のみの場合、domain制限に基づいてtranslateExtentを計算
    const defaultWidth = 400;
    const defaultHeight = 300;
    // 凡例の位置に応じてマージンを調整（zoomOptions計算用）
    const legendPos = config.scales?.color?.legendPosition || 'right';
    const baseMargin = { top: 20, right: 20, bottom: 40, left: 50 };
    const margin = legendPos === 'right' ? { ...baseMargin, right: 100 } :
                   legendPos === 'left' ? { ...baseMargin, left: 100 } :
                   legendPos === 'top' ? { ...baseMargin, top: 60 } :
                   legendPos === 'bottom' ? { ...baseMargin, bottom: 80 } :
                   baseMargin;
    const innerWidth = defaultWidth - margin.left - margin.right;
    const innerHeight = defaultHeight - margin.top - margin.bottom;

    // domain制限がない場合はtranslateExtentなし（ContinuousScaleDefのみ）
    const xAxisDef = config.scales?.x;
    const yAxisDef = config.scales?.y;
    const xDomainConfig = isContinuousScale(xAxisDef) ? xAxisDef.domain : undefined;
    const yDomainConfig = isContinuousScale(yAxisDef) ? yAxisDef.domain : undefined;
    const hasDomainLimit = xDomainConfig?.min !== undefined || xDomainConfig?.max !== undefined ||
                           yDomainConfig?.min !== undefined || yDomainConfig?.max !== undefined;

    if (!hasDomainLimit) {
      return {
        scaleExtent: [0.5, 10] as [number, number],
        enableDoubleClickReset: true,
      };
    }

    // データ座標のdomain（完全な範囲、制限なし）
    const xDataDomain = xDomain as [number, number];
    const yDataDomain = yDomain as [number, number];

    // 完全なデータdomainでスケールを作成（translateExtent計算用）
    const xScale = createNumericScale(
      config.scales?.x?.type || 'linear',
      xDataDomain,
      undefined
    ).range([0, innerWidth]);

    const yScale = createNumericScale(
      config.scales?.y?.type || 'linear',
      yDataDomain,
      undefined
    ).range([innerHeight, 0]);

    // domain制限値（指定がなければデータのdomain）
    const xMin = xDomainConfig?.min ?? xDataDomain[0];
    const xMax = xDomainConfig?.max ?? xDataDomain[1];
    const yMin = yDomainConfig?.min ?? yDataDomain[0];
    const yMax = yDomainConfig?.max ?? yDataDomain[1];

    // 制限したいdomain値を、完全なデータdomainのスケールでピクセル座標に変換
    const xMinPx = xScale(xMin);
    const xMaxPx = xScale(xMax);
    const yMinPx = yScale(yMin);  // 注意：Y軸は上下反転（大きい値が上）
    const yMaxPx = yScale(yMax);

    // translateExtentはSVGの座標系で指定するため、marginを加算
    // g要素はtranslate(margin.left, margin.top)されているため
    const translateExtentWithMargin: [[number, number], [number, number]] = [
      [margin.left + xMinPx, margin.top + yMaxPx],
      [margin.left + xMaxPx, margin.top + yMinPx]
    ];

    // extentはビューポートのサイズを指定（デフォルトサイズを使用）
    // これにより、translateExtentとの相対的な関係でパン制限が適用される
    const extentValue: [[number, number], [number, number]] = [
      [0, 0],
      [defaultWidth, defaultHeight]
    ];

    return {
      scaleExtent: [0.5, 10] as [number, number],
      translateExtent: translateExtentWithMargin,
      extent: extentValue,
      enableDoubleClickReset: true,
    };
  }, [isXCategorical, isYCategorical, xDomain, yDomain, config.scales?.x, config.scales?.y, config.scales?.color?.legendPosition]);

  // 凡例の位置に応じてマージンを動的に調整
  const marginWithLegend = React.useMemo(() => {
    const baseMargin = { top: 20, right: 20, bottom: 40, left: 50 };
    const legendPosition = config.scales?.color?.legendPosition || 'right';
    if (legendPosition === 'right') {
      return { ...baseMargin, right: 100 };
    } else if (legendPosition === 'left') {
      return { ...baseMargin, left: 100 };
    } else if (legendPosition === 'top') {
      return { ...baseMargin, top: 60 };
    } else if (legendPosition === 'bottom') {
      return { ...baseMargin, bottom: 80 };
    }
    return baseMargin;
  }, [config.scales?.color?.legendPosition]);

  // useD3Chartで基本的な構造を作成
  // defaultWidth/Heightは初期値のみ。実際のサイズはResizeObserverでコンテナから取得
  const { containerRef, svgRef } = useD3Chart({
    defaultWidth: 600,
    defaultHeight: 400,
    monitorWidth: true,   // 幅はコンテナに応じて自動調整
    monitorHeight: true,  // 高さもコンテナに応じて自動調整
    margin: marginWithLegend,
    enableZoom: zoomOptions !== undefined,
    zoomOptions,
    setup: ({ svg, g }) => {
      // クリップパスを定義（タイルなどが描画領域外に出ないように）
      const clipId = `plot-area-clip-${Math.random().toString(36).substring(7)}`;
      const defs = svg.append('defs');
      const clipPath = defs.append('clipPath')
        .attr('id', clipId);
      const clipRect = clipPath.append('rect');

      // 各レイヤー用のグループを作成（クリップパスを適用）
      // 軸より先に作成することで、軸がレイヤーの上に描画される
      const layerGroups = layerData.map((_, i) =>
        g.append('g')
          .attr('class', `layer-${i}`)
          .attr('clip-path', `url(#${clipId})`)
      );

      // 密度曲線用のグループ
      const densityGroup = g.append('g').attr('class', 'density');

      // 軸グループ（レイヤーの上に描画）
      const xAxisGroup = g.append('g')
        .attr('class', 'x-axis');

      const yAxisGroup = g.append('g')
        .attr('class', 'y-axis');

      const y2AxisGroup = g.append('g')
        .attr('class', 'y2-axis');

      // 軸タイトルを追加（設定されたタイトル、または自動生成されたタイトルを使用）
      let xAxisLabel: d3.Selection<SVGTextElement, unknown, null, undefined> | null = null;
      const xTitle = config.scales?.x?.title || autoAxisTitles.xTitle;
      if (xTitle) {
        xAxisLabel = g.append('text')
          .attr('class', 'x-axis-label')
          .attr('text-anchor', 'middle')
          .attr('fill', '#666')
          .style('font-size', '12px')
          .text(xTitle);
      }

      let yAxisLabel: d3.Selection<SVGTextElement, unknown, null, undefined> | null = null;
      const yTitle = config.scales?.y?.title || autoAxisTitles.yTitle;
      if (yTitle) {
        yAxisLabel = g.append('text')
          .attr('class', 'y-axis-label')
          .attr('text-anchor', 'middle')
          .attr('fill', '#666')
          .style('font-size', '12px')
          .text(yTitle);
      }

      let y2AxisLabel: d3.Selection<SVGTextElement, unknown, null, undefined> | null = null;
      // Secondary Y軸タイトル（y2が設定されている場合のみ）
      if (config.scales?.y2) {
        const y2Title = config.scales.y2.title || 'Y2';
        y2AxisLabel = g.append('text')
          .attr('class', 'y2-axis-label')
          .attr('text-anchor', 'middle')
          .attr('fill', '#666')
          .style('font-size', '12px')
          .text(y2Title);
      }

      // 凡例グループ（SVGの外側に配置）
      const legendGroup = g.append('g').attr('class', 'legend');

      return {
        xAxisGroup,
        yAxisGroup,
        y2AxisGroup,
        xAxisLabel,
        yAxisLabel,
        y2AxisLabel,
        layerGroups,
        legendGroup,
        densityGroup,
        clipRect,
      };
    },
    render: ({ refs, innerWidth, innerHeight, currentTransform }) => {
      const { xAxisGroup, yAxisGroup, y2AxisGroup, xAxisLabel, yAxisLabel, y2AxisLabel, layerGroups, legendGroup, densityGroup, clipRect } = refs;

      // クリップパスのサイズを更新
      clipRect
        .attr('width', innerWidth)
        .attr('height', innerHeight);

      // 座標系の取得
      const coordType = config.coord?.type || 'cartesian';

      // flip座標系の場合、ドメインとカテゴリカルフラグを入れ替え
      const effectiveXDomain = coordType === 'flip' ? yDomain : xDomain;
      const effectiveYDomain = coordType === 'flip' ? xDomain : yDomain;
      const effectiveIsXCategorical = coordType === 'flip' ? isYCategorical : isXCategorical;
      const effectiveIsYCategorical = coordType === 'flip' ? isXCategorical : isYCategorical;

      // 軸のカラム型を取得（Int64の場合に整数tickフォーマットを適用するため）
      const firstLayerAes = layersWithAes[0]?.aes;
      const xColumnId = firstLayerAes?.x as string | undefined;
      const yColumnId = firstLayerAes?.y as string | undefined;
      const xColumn = xColumnId ? dataset.schema.columns.find(c => c.id === xColumnId) : undefined;
      const yColumn = yColumnId ? dataset.schema.columns.find(c => c.id === yColumnId) : undefined;
      // flip座標系の場合、effectiveなカラム型も入れ替え
      const effectiveXColumnType = coordType === 'flip' ? yColumn?.type : xColumn?.type;
      const effectiveYColumnType = coordType === 'flip' ? xColumn?.type : yColumn?.type;

      // スケールを再作成（設定変更に対応）
      // カテゴリカルスケールと数値スケールを分けて管理
      type NumericScale = d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | d3.ScalePower<number, number>;
      type CategoricalScale = d3.ScaleBand<string>;

      let xScaleNumeric: NumericScale | null = null;
      let xScaleCategorical: CategoricalScale | null = null;
      let yScaleNumeric: NumericScale | null = null;
      let yScaleCategorical: CategoricalScale | null = null;
      let y2ScaleNumeric: NumericScale | null = null;

      if (effectiveIsXCategorical) {
        xScaleCategorical = createCategoricalScale(effectiveXDomain as string[], [0, innerWidth]);
      } else {
        xScaleNumeric = createNumericScale(
          config.scales?.x?.type || 'linear',
          effectiveXDomain as [number, number],
          config.scales?.x
        ).range([0, innerWidth]);
      }

      if (effectiveIsYCategorical) {
        yScaleCategorical = createCategoricalScale(effectiveYDomain as string[], [innerHeight, 0]);
      } else {
        yScaleNumeric = createNumericScale(
          config.scales?.y?.type || 'linear',
          effectiveYDomain as [number, number],
          config.scales?.y
        ).range([innerHeight, 0]);
      }

      // Secondary Y軸のスケール（y2が設定されている場合のみ）
      // flip座標系の場合は無効化
      if (config.scales?.y2 && y2Domain && coordType !== 'flip') {
        y2ScaleNumeric = createNumericScale(
          config.scales.y2.type || 'linear',
          y2Domain as [number, number],
          config.scales.y2
        ).range([innerHeight, 0]);
      }

      // ズーム適用後のスケールを計算（カテゴリカルスケールはズーム不可）
      const zoomTransform = currentTransform || d3.zoomIdentity;
      const currentXScale = xScaleCategorical || (xScaleNumeric ? zoomTransform.rescaleX(xScaleNumeric) : d3.scaleLinear());
      const currentYScale = yScaleCategorical || (yScaleNumeric ? zoomTransform.rescaleY(yScaleNumeric) : d3.scaleLinear());
      const currentY2Scale = y2ScaleNumeric ? zoomTransform.rescaleY(y2ScaleNumeric) : null;

      // 軸の描画（ズーム適用後のスケールを使用）
      // X軸
      if (effectiveIsXCategorical && xScaleCategorical) {
        const xAxis = d3.axisBottom(xScaleCategorical);
        xAxisGroup
          .attr('transform', `translate(0, ${innerHeight})`)
          .call(xAxis);

        // ラベルの最大文字数に基づいて回転を判定
        // Issue#236: カテゴリ数だけでなく、ラベル幅とバンド幅を比較
        const labels = xScaleCategorical.domain();
        const maxLabelLength = Math.max(...labels.map(l => String(l).length));
        const bandwidth = xScaleCategorical.bandwidth();
        // 推定ラベル幅: 1文字あたり約7px（デフォルトフォントサイズでの近似値）
        const estimatedLabelWidth = maxLabelLength * 7;

        if (estimatedLabelWidth > bandwidth) {
          xAxisGroup.selectAll('text')
            .attr('transform', 'rotate(-45)')
            .style('text-anchor', 'end');
        }
      } else if (xScaleNumeric) {
        const xAxis = d3.axisBottom(currentXScale as d3.AxisScale<number>);
        const xScaleDef = config.scales?.x;

        // パネル幅に基づいて推奨tick数を計算（ユーザー指定がない場合）
        const suggestedTickCount = Math.floor(innerWidth / 50);
        const tickCount = (isContinuousScale(xScaleDef) && xScaleDef.ticks?.count)
          ? xScaleDef.ticks.count
          : suggestedTickCount;

        xAxis.ticks(tickCount);

        // Int64の場合は整数フォーマットを適用（ユーザー指定のformatがない場合）
        if (effectiveXColumnType === 'int64' && !(isContinuousScale(xScaleDef) && xScaleDef.ticks?.format)) {
          xAxis.tickFormat(d3.format('d'));
        } else if (isContinuousScale(xScaleDef) && xScaleDef.ticks?.format) {
          xAxis.tickFormat(d3.format(xScaleDef.ticks.format));
        }
        xAxisGroup
          .attr('transform', `translate(0, ${innerHeight})`)
          .call(xAxis);
      }

      // Y軸
      if (effectiveIsYCategorical && yScaleCategorical) {
        const yAxis = d3.axisLeft(yScaleCategorical);
        yAxisGroup.call(yAxis);
      } else if (yScaleNumeric) {
        const yAxis = d3.axisLeft(currentYScale as d3.AxisScale<number>);
        const yScaleDef = config.scales?.y;
        if (isContinuousScale(yScaleDef) && yScaleDef.ticks?.count) {
          yAxis.ticks(yScaleDef.ticks.count);
        }
        // Int64の場合は整数フォーマットを適用（ユーザー指定のformatがない場合）
        if (effectiveYColumnType === 'int64' && !(isContinuousScale(yScaleDef) && yScaleDef.ticks?.format)) {
          yAxis.tickFormat(d3.format('d'));
        } else if (isContinuousScale(yScaleDef) && yScaleDef.ticks?.format) {
          yAxis.tickFormat(d3.format(yScaleDef.ticks.format));
        }
        yAxisGroup.call(yAxis);
      }

      // Secondary Y軸（y2が設定され、flip座標系でない場合のみ）
      if (currentY2Scale && coordType !== 'flip') {
        const y2Axis = d3.axisRight(currentY2Scale as d3.AxisScale<number>);
        const y2ScaleDef = config.scales?.y2;
        if (isContinuousScale(y2ScaleDef) && y2ScaleDef.ticks?.count) {
          y2Axis.ticks(y2ScaleDef.ticks.count);
        }
        // Secondary Y軸もInt64の場合は整数フォーマットを適用
        // 注意：y2は別のカラムを参照する可能性があるが、現状はyと同じカラム型を使用
        if (effectiveYColumnType === 'int64' && !(isContinuousScale(y2ScaleDef) && y2ScaleDef.ticks?.format)) {
          y2Axis.tickFormat(d3.format('d'));
        } else if (isContinuousScale(y2ScaleDef) && y2ScaleDef.ticks?.format) {
          y2Axis.tickFormat(d3.format(y2ScaleDef.ticks.format));
        }
        y2AxisGroup
          .attr('transform', `translate(${innerWidth}, 0)`)
          .call(y2Axis);
      } else {
        // Secondary Y軸が無効な場合は非表示
        y2AxisGroup.selectAll('*').remove();
      }

      // 軸ラベルの位置とテキストを更新
      // flip座標系の場合、xAxisLabelとyAxisLabelの位置とテキストを入れ替え
      const xTitle = config.scales?.x?.title || autoAxisTitles.xTitle;
      const yTitle = config.scales?.y?.title || autoAxisTitles.yTitle;

      if (coordType === 'flip') {
        // flip時：X aestheticは縦軸に、Y aestheticは横軸に表示される
        if (xAxisLabel && xTitle) {
          xAxisLabel
            .attr('transform', 'rotate(-90)')
            .attr('x', -innerHeight / 2)
            .attr('y', -35)
            .text(xTitle);
        }
        if (yAxisLabel && yTitle) {
          yAxisLabel
            .attr('transform', '')
            .attr('x', innerWidth / 2)
            .attr('y', innerHeight + 35)
            .text(yTitle);
        }
        // flip時はSecondary Y軸ラベルを非表示
        if (y2AxisLabel) {
          y2AxisLabel.attr('opacity', 0);
        }
      } else {
        // 通常：X aestheticは横軸に、Y aestheticは縦軸に表示される
        if (xAxisLabel && xTitle) {
          xAxisLabel
            .attr('transform', '')
            .attr('x', innerWidth / 2)
            .attr('y', innerHeight + 35)
            .text(xTitle);
        }
        if (yAxisLabel && yTitle) {
          yAxisLabel
            .attr('transform', 'rotate(-90)')
            .attr('x', -innerHeight / 2)
            .attr('y', -35)
            .text(yTitle);
        }
        // Secondary Y軸ラベルの位置設定
        if (y2AxisLabel && currentY2Scale) {
          const y2Title = config.scales?.y2?.title || 'Y2';
          y2AxisLabel
            .attr('opacity', 1)
            .attr('transform', 'rotate(-90)')
            .attr('x', -innerHeight / 2)
            .attr('y', innerWidth + 45)  // 右側に配置
            .text(y2Title);
        } else if (y2AxisLabel) {
          y2AxisLabel.attr('opacity', 0);
        }
      }

      // 座標変換ヘルパー関数（カテゴリカル・数値両対応）
      const getX = (d: { x: string | number; y: number }) => coordType === 'flip' ? d.y : d.x;
      const getY = (d: { x: string | number; y: number }) => coordType === 'flip' ? d.x : d.y;

      // スケール呼び出しヘルパー関数（カテゴリカル・数値両対応）
      const callXScale = (value: string | number): number => {
        if (effectiveIsXCategorical && xScaleCategorical) {
          return (xScaleCategorical(String(value)) ?? 0) + xScaleCategorical.bandwidth() / 2;
        }
        // 数値スケールとして扱う（カテゴリカルでない場合、valueは必ずnumber）
        const scale = currentXScale as NumericScale;
        return scale(value as number);
      };

      // 各レイヤーを描画
      layerData.forEach(({ layer, points, scales }, i) => {
        const layerGroup = layerGroups[i];

        // レイヤーのyAxis設定に応じて適切なY軸スケールを選択
        const useSecondaryYAxis = layer.yAxis === 'secondary';
        const layerCurrentYScale = (useSecondaryYAxis && currentY2Scale) ? currentY2Scale : currentYScale;

        // Y軸スケール呼び出しヘルパー関数をレイヤーごとに作成
        const layerCallYScale = (value: string | number): number => {
          if (effectiveIsYCategorical && yScaleCategorical) {
            return (yScaleCategorical(String(value)) ?? 0) + yScaleCategorical.bandwidth() / 2;
          }
          // 数値スケールとして扱う（カテゴリカルでない場合、valueは必ずnumber）
          const scale = layerCurrentYScale as NumericScale;
          return scale(value as number);
        };

        // fillがHex色かどうかを判定
        const fillAesValue = layersWithAes[i]?.aes?.fill;
        const fixedFillColor = (typeof fillAesValue === 'string' && fillAesValue.startsWith('#'))
          ? fillAesValue
          : undefined;

        // GeometryRenderContextを構築
        const context: GeometryRenderContext = {
          layerGroup,
          points,
          layer,
          scales,
          coordType,
          effectiveIsXCategorical,
          effectiveIsYCategorical,
          xScaleCategorical,
          yScaleCategorical,
          xScaleNumeric,
          yScaleNumeric,
          currentXScale,
          currentYScale: layerCurrentYScale,  // レイヤーごとのY軸スケール
          getX,
          getY,
          callXScale,
          callYScale: layerCallYScale,  // レイヤーごとのY軸スケール呼び出し
          selectedRows,
          onPointClick,
          fixedFillColor,
        };

        // Geomタイプに応じた描画関数を呼び出し
        if (layer.geom.type === 'point') {
          renderPointGeometry(context);
        } else if (layer.geom.type === 'line') {
          renderLineGeometry(context);
        } else if (layer.geom.type === 'bar') {
          renderBarGeometry(context);
        } else if (layer.geom.type === 'area') {
          renderAreaGeometry(context);
        } else if (layer.geom.type === 'tile') {
          renderTileGeometry(context);
        }
      });

      // 凡例の描画
      const legendPosition = config.scales?.color?.legendPosition || 'right';
      if (legendPosition !== 'none') {
        // fillまたはcolorスケールを持つレイヤーを取得（fillを優先）
        const layersWithFill = layerData.filter(({ scales }) => scales.fill !== null && scales.fill !== undefined);
        const layersWithColor = layerData.filter(({ scales }) => scales.color !== null && scales.color !== undefined);

        // fillが存在すればfillを使用、なければcolorを使用
        const useFill = layersWithFill.length > 0;
        const targetLayers = useFill ? layersWithFill : layersWithColor;

        if (targetLayers.length > 0) {
          // 使用するスケールとaesthetic名を決定
          const targetScale = useFill ? targetLayers[0].scales.fill! : targetLayers[0].scales.color!;
          const aestheticName = useFill ? 'fill' : 'color';

          // レイヤーのインデックスを取得して、対応するaestheticマッピングから列名を取得
          const layerIndex = layerData.indexOf(targetLayers[0]);
          const layerAes = layersWithAes[layerIndex]?.aes;
          const columnId = useFill ? layerAes?.fill : layerAes?.color;

          // 列名を取得（ColumnIdは文字列）
          let columnName: string | undefined;
          if (columnId && typeof columnId === 'string') {
            const column = dataset.schema.columns.find(col => col.id === columnId);
            columnName = column?.name;
          }

          // デバッグログ
          console.log('[Legend] useFill:', useFill, 'layerIndex:', layerIndex, 'columnId:', columnId, 'columnName:', columnName, 'aestheticName:', aestheticName);

          const categories = targetScale.domain();
          // 凡例タイトル: 設定値 > 列名 > aesthetic名
          const legendTitle = config.scales?.color?.legendTitle || columnName || aestheticName;

          // 凡例をクリア
          legendGroup.selectAll('*').remove();

          // 凡例タイトル
          legendGroup.append('text')
            .attr('class', 'legend-title')
            .attr('font-size', '12px')
            .attr('font-weight', 'bold')
            .attr('fill', '#333')
            .text(legendTitle);

          // 凡例アイテム
          const legendItems = legendGroup.selectAll('.legend-item')
            .data(categories)
            .enter()
            .append('g')
            .attr('class', 'legend-item');

          // 色サンプル（矩形）
          legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', d => targetScale(d));

          // ラベル
          legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .attr('font-size', '11px')
            .attr('fill', '#666')
            .text(d => String(d));

          // 凡例の位置とレイアウト
          if (legendPosition === 'right') {
            // 右側に縦並び
            legendGroup.attr('transform', `translate(${innerWidth + 20}, 0)`);
            legendItems.attr('transform', (_d, i) => `translate(0, ${20 + i * 20})`);
          } else if (legendPosition === 'left') {
            // 左側に縦並び
            legendGroup.attr('transform', `translate(-60, 0)`);
            legendItems.attr('transform', (_d, i) => `translate(0, ${20 + i * 20})`);
          } else if (legendPosition === 'top') {
            // 上側に横並び
            legendGroup.attr('transform', `translate(0, -30)`);
            legendItems.attr('transform', (_d, i) => `translate(${i * 80}, 20)`);
          } else if (legendPosition === 'bottom') {
            // 下側に横並び
            legendGroup.attr('transform', `translate(0, ${innerHeight + 50})`);
            legendItems.attr('transform', (_d, i) => `translate(${i * 80}, 20)`);
          }
        } else {
          // カラースケールがない場合は凡例をクリア
          legendGroup.selectAll('*').remove();
        }
      } else {
        // legendPosition === 'none'の場合は凡例をクリア
        legendGroup.selectAll('*').remove();
      }

      // 密度曲線はレイヤーとして実装するため、ここでは描画しない
      densityGroup.selectAll('*').remove();
    },
    dependencies: [layersWithAes, layerData, selectedRows, config.scales?.x, config.scales?.y, config.scales?.color, config.coord, dataset],
  });

  // データポイントがない場合
  const hasPoints = layerData.some(({ points }) => points.length > 0);
  if (!hasPoints) {
    return (
      <div style={{ padding: '20px', color: '#666' }}>
        No valid data points. Please select numeric columns for X and Y axes.
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <div style={{ position: 'relative' }}>
        <svg ref={svgRef} style={{ width: '100%', height: '100%', overflow: 'visible' }} />

        {/* オーバーレイボタン */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          padding: '2px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          pointerEvents: 'none'
        }}>
          {/* ズームが有効な場合のみPan & Zoom modeアイコンを表示 */}
          {zoomOptions !== undefined && (
            <div
              title="Pan & Zoom mode (Ctrl/Cmd + Wheel to zoom, drag to pan, double-click to reset)"
              style={{
                width: '28px',
                height: '28px',
                padding: '0',
                border: 'none',
                borderRadius: '4px',
                background: 'rgba(66, 133, 244, 0.2)',
                cursor: 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto'
              }}
            >
              {/* パンアイコン（移動矢印） */}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z"
                  fill="#4285f4" />
              </svg>
            </div>
          )}
          <ClearSelectionButton
            onClear={() => onPointClick?.([], false)}
            hasSelection={selectedRows.length > 0}
            style={{
              marginLeft: zoomOptions !== undefined ? '4px' : '0',
              borderLeft: zoomOptions !== undefined ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
              pointerEvents: 'auto'
            }}
          />
        </div>
      </div>
    </div>
  );
}
